import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatLibelleForDisplay } from '../utils/libelleFormatter';
import { isAutoGeneratedHonoLibelle } from '../utils/honoAutoLibelle';
import ClientOperationAddForm from './ClientOperationAddForm';
import './NHPage.css';
import './CgmFilterToolbar.css';
import './ClientChargesModern.css';
import { authJsonHeaders } from '../utils/authHeaders';
import {
  getActiveCaisseYear,
  subscribeCaisseYear,
  syncActiveCaisseYearWithCalendar,
} from '../utils/caisseActiveYear';

function getChargesPageNumbers(currentPage, totalPages) {
  const pageNumbers = [];
  const maxVisible = 6;
  if (totalPages <= maxVisible) {
    for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
  } else if (currentPage <= 3) {
    for (let i = 1; i <= 5; i++) pageNumbers.push(i);
    pageNumbers.push('...');
    pageNumbers.push(totalPages);
  } else if (currentPage >= totalPages - 2) {
    pageNumbers.push(1);
    pageNumbers.push('...');
    for (let i = totalPages - 4; i <= totalPages; i++) pageNumbers.push(i);
  } else {
    pageNumbers.push(1);
    pageNumbers.push('...');
    for (let i = currentPage - 1; i <= currentPage + 1; i++) pageNumbers.push(i);
    pageNumbers.push('...');
    pageNumbers.push(totalPages);
  }
  return pageNumbers;
}

const HONO_PERIODE_LABELS = {
  mensuel: 'mensuel',
  trimestriel: 'trimestriel',
  semestriel: 'semestriel',
  annuel: 'annuel'
};

export default function ClientCharges() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isCaissier, isAdmin } = useAuth();
  const canEditOperations = typeof isAdmin === 'function' ? isAdmin() : false;
  const [client, setClient] = useState(null);
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [annee, setAnnee] = useState(() => syncActiveCaisseYearWithCalendar());
  const [filterLibelle, setFilterLibelle] = useState(''); // Filtre par libellé ('' = Tous)
  const [editingCharge, setEditingCharge] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isCarteBancaire, setIsCarteBancaire] = useState(false);
  const [chargesWithExpenses, setChargesWithExpenses] = useState(new Set());
  const [chargesWithClientExpenses, setChargesWithClientExpenses] = useState(new Set());
  const [chargesWithCgmExpenses, setChargesWithCgmExpenses] = useState(new Set());
  const [chargesWithClientAndCgmExpenses, setChargesWithClientAndCgmExpenses] = useState(new Set());
  const [chargesWithRetraitCgm, setChargesWithRetraitCgm] = useState(new Set());
  const [chargesWithFondClientDepot, setChargesWithFondClientDepot] = useState(new Set());
  const [chargesWithBanqueRestauree, setChargesWithBanqueRestauree] = useState(new Set());
  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 16), // Format datetime-local
    libelle: '',
    libelleCustom: '', // Pour le libellé personnalisé
    montant: '',
    avance: ''
  });
  /** Récap hono (liste clients) : période + montant, dernière ligne en base */
  const [honoRecap, setHonoRecap] = useState(null);
  /** Récap fond client chez CGM (solde), null si aucune opération fond */
  const [fondClientRecap, setFondClientRecap] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // #region agent log
  const logEvent = (payload) => {
    fetch('http://127.0.0.1:7242/ingest/4c19617c-ffd9-4631-93bb-12be75be63ad', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: 'debug-session',
        runId: 'run1',
        ...payload,
        timestamp: Date.now()
      })
    }).catch(() => { });
  };
  // #endregion

  useEffect(() => {
    fetchClientCharges();
  }, [id, annee]);

  useEffect(() => {
    return subscribeCaisseYear((y) => {
      const year = Number(y) || getActiveCaisseYear();
      setAnnee(year);
    });
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterLibelle, annee, itemsPerPage]);

  // Vérifier quelles charges ont déjà des dépenses
  useEffect(() => {
    if (charges.length > 0) {
      checkChargesWithExpenses();
    }
  }, [charges]);

  // Vérifier quelles charges ont déjà des dépenses (client ou bureau)
  const checkChargesWithExpenses = async () => {
    try {
      const chargeIds = charges
        .filter(charge => !charge.isPrecedent)
        .map(charge => charge.id);

      if (chargeIds.length === 0) return;

      // Vérifier chaque charge individuellement
      const chargesWithClientExpensesSet = new Set();
      const chargesWithCgmExpensesSet = new Set();
      const chargesWithClientAndCgmExpensesSet = new Set();
      const chargesWithFondClientDepotSet = new Set();

      // Récupérer tous les dépôts caisse CGM une seule fois pour optimiser
      let allCaisseDepots = [];
      try {
        const depotsRes = await fetch('/api/caisse-cgm/depots?annee=' + annee);
        const depotsData = await depotsRes.json();
        if (depotsData.success && Array.isArray(depotsData.depots)) {
          allCaisseDepots = depotsData.depots;
        }
      } catch (e) {
        console.error('Erreur lors de la récupération des dépôts caisse CGM:', e);
      }

      for (const chargeId of chargeIds) {
        try {
          const chargeObj = charges.find(c => c.id === chargeId);
          if (!chargeObj) continue;

          // Vérifier les dépenses client - utiliser l'endpoint spécifique avec charge_id
          try {
            const clientResponse = await fetch(`/api/depenses?charge_id=${chargeId}&client_id=${id}`);
            const clientData = await clientResponse.json();
            // Vérifier que c'est bien une dépense client (type: 'client' ou dans depenses_client)
            if (clientData.success && clientData.depenses && Array.isArray(clientData.depenses)) {
              const hasClientExpense = clientData.depenses.some(dep =>
                dep.type === 'client' || dep.charge_id === chargeId
              );
              if (hasClientExpense) {
                chargesWithClientExpensesSet.add(chargeId);
              }
            }
          } catch (err) {
            console.error(`Erreur vérification dépenses client pour charge ${chargeId}:`, err);
          }

          // Détection précise d'une dépense CGM liée à cette charge:
          // On considère qu'une dépense bureau correspond à la charge si:
          // - le libellé ou la description contient le préfixe [CGM] ET le libellé exact de la charge
          // - et la date (jour) correspond exactement
          // - et le bénéficiaire correspond au nom du client
          // - et le montant correspond (pour éviter les fausses correspondances)
          try {
            const bureauResponse = await fetch(`/api/depenses/bureau`);
            const bureauData = await bureauResponse.json();
            if (bureauData.success && Array.isArray(bureauData.depenses)) {
              const chargeLib = (chargeObj.libelle || '').toUpperCase();
              const chargeMontant = parseFloat(chargeObj.montant || 0);
              const chargeDate = chargeObj.date ? new Date(chargeObj.date) : (chargeObj.date_creation ? new Date(chargeObj.date_creation) : null);
              const chDay = chargeDate ? `${chargeDate.getUTCFullYear()}-${String(chargeDate.getUTCMonth() + 1).padStart(2, '0')}-${String(chargeDate.getUTCDate()).padStart(2, '0')}` : null;

              const hasMatchingBureauExpense = bureauData.depenses.some(depense => {
                const raw = (depense.description || depense.libelle || '').toUpperCase();
                const depDate = new Date(depense.date || depense.date_operation);
                const depDay = `${depDate.getUTCFullYear()}-${String(depDate.getUTCMonth() + 1).padStart(2, '0')}-${String(depDate.getUTCDate()).padStart(2, '0')}`;
                const sameDay = chDay && depDay === chDay;
                const sameBenef = (depense.beneficiaire || depense.nom_beneficiaire || '').toUpperCase() === ((client?.nom || '').toUpperCase());
                const sameMontant = Math.abs(parseFloat(depense.montant || 0) - chargeMontant) < 0.01; // Tolérance pour les arrondis
                const hasCgmPrefix = raw.includes('[CGM]');
                const hasChargeLibelle = chargeLib && raw.includes(chargeLib);

                // Correspondance stricte: préfixe CGM, libellé, date, bénéficiaire et montant
                return hasCgmPrefix && hasChargeLibelle && sameDay && sameBenef && sameMontant;
              });

              if (hasMatchingBureauExpense) {
                chargesWithCgmExpensesSet.add(chargeId);
              }
            }
          } catch (err) {
            console.error(`Erreur vérification dépenses CGM pour charge ${chargeId}:`, err);
          }

          // Vérifier si un dépôt caisse CGM a déjà été créé pour une charge "PAYÉ PAR FOND CLIENT"
          try {
            const chargeLib = (chargeObj.libelle || '').toUpperCase();
            const isPayeParFondClient = chargeLib.includes('PAYE PAR FOND CLIENT') || chargeLib.includes('PAYÉ PAR FOND CLIENT');
            if (isPayeParFondClient && allCaisseDepots.length > 0) {
              const montantDepotCharge = parseFloat(chargeObj.avance || chargeObj.montant || 0);
              const clientNameUpper = (client?.nom || '').toUpperCase();

              const hasMatchingDepot = allCaisseDepots.some(dep => {
                const depComment = (dep.commentaire || '').toUpperCase();
                const depMontant = parseFloat(dep.montant || 0);
                const sameMontant = Math.abs(depMontant - montantDepotCharge) < 0.01;
                const hasClient = clientNameUpper && depComment.includes(clientNameUpper);
                const hasLibelle = chargeLib && depComment.includes('PAYE PAR FOND CLIENT');
                return sameMontant && hasClient && hasLibelle;
              });

              if (hasMatchingDepot) {
                chargesWithFondClientDepotSet.add(chargeId);
              }
            }
          } catch (err) {
            console.error(`Erreur vérification dépôts fond client pour charge ${chargeId}:`, err);
          }

          // Si une charge a à la fois des dépenses client et bureau, elle est "Client et CGM"
          if (chargesWithClientExpensesSet.has(chargeId) && chargesWithCgmExpensesSet.has(chargeId)) {
            chargesWithClientAndCgmExpensesSet.add(chargeId);
            // Harmoniser les états des boutons: on ne montre pas "Stocké" seulement côté Client
            // si elle est aussi CGM. Cela évite l'ambiguïté vue pour HONORAIRES REÇU.
            chargesWithClientExpensesSet.delete(chargeId);
            chargesWithCgmExpensesSet.delete(chargeId);
          }
        } catch (error) {
          console.error(`Erreur lors de la vérification de la charge ${chargeId}:`, error);
        }
      }

      // Vérifier les restaurations banque déjà effectuées (endpoint renvoie [] si legacy désactivé)
      const banqueRestaureeSet = new Set();
      const carteChargeIds = chargeIds.filter(cid => {
        const c = charges.find(ch => ch.id === cid);
        if (!c) return false;
        const lib = String(c.libelle || '').toUpperCase();
        return lib.includes('[CARTE BANCAIRE]') || lib.includes('[CARTE]');
      });
      if (carteChargeIds.length > 0) {
        try {
          const restoCheckRes = await fetch(`/api/caisse-cgm/check-restaurations?client_id=${id}&charge_ids=${carteChargeIds.join(',')}`);
          const restoCheckData = await restoCheckRes.json();
          if (restoCheckData.success && Array.isArray(restoCheckData.restored)) {
            restoCheckData.restored.forEach(cid => banqueRestaureeSet.add(cid));
          }
        } catch (e) {
          console.error('Erreur vérification restaurations banque:', e);
        }
      }

      setChargesWithClientExpenses(chargesWithClientExpensesSet);
      setChargesWithCgmExpenses(chargesWithCgmExpensesSet);
      setChargesWithClientAndCgmExpenses(chargesWithClientAndCgmExpensesSet);
      setChargesWithFondClientDepot(chargesWithFondClientDepotSet);
      setChargesWithBanqueRestauree(prev => new Set([...prev, ...banqueRestaureeSet]));
    } catch (error) {
      console.error('Erreur lors de la vérification des dépenses:', error);
    }
  };

  // Écouter l'événement du bouton "Ajouter charge" de la navbar
  useEffect(() => {
    const handleOpenAddCharge = () => {
      setShowForm(true);
    };

    window.addEventListener('open-add-charge-modal', handleOpenAddCharge);
    return () => window.removeEventListener('open-add-charge-modal', handleOpenAddCharge);
  }, []);

  const fetchClientCharges = async () => {
    try {
      setLoading(true);
      setError('');

      // Récupérer les informations du client
      const clientRes = await fetch(`/api/clients/${id}`);
      const clientData = await clientRes.json();

      if (!clientData.success) {
        setError('Client non trouvé');
        setHonoRecap(null);
        setFondClientRecap(null);
        return;
      }

      setClient(clientData.client);

      setHonoRecap(null);
      setFondClientRecap(null);
      try {
        const honoRes = await fetch(`/api/client-hono-periode/client/${id}`);
        if (honoRes.ok) {
          const honoData = await honoRes.json();
          if (honoData.success && Array.isArray(honoData.rows) && honoData.rows.length > 0) {
            const row = honoData.rows[0];
            const m = parseFloat(String(row.montant).replace(',', '.'));
            if (!Number.isNaN(m) && HONO_PERIODE_LABELS[row.periode_type]) {
              setHonoRecap({
                periode_type: row.periode_type,
                montantFormatted: new Intl.NumberFormat('fr-FR', {
                  minimumFractionDigits: 3,
                  maximumFractionDigits: 3
                }).format(Math.abs(m))
              });
            }
          }
        }
      } catch {
        setHonoRecap(null);
      }

      // Fond client chez CGM (toutes années) — n'afficher que s'il y a au moins un dépôt fond
      try {
        const fondRes = await fetch(`/api/charges-mensuelles/client/${id}?all=true`);
        if (fondRes.ok) {
          const fondData = await fondRes.json();
          const allCharges = Array.isArray(fondData.charges) ? fondData.charges : [];
          let totalFond = 0;
          let totalPaye = 0;
          let hasFond = false;
          allCharges.forEach((c) => {
            if (c.isPrecedent) return;
            const lib = String(c.libelle || '').toUpperCase();
            if (lib.includes('ARGENT CLIENT CHEZ CGM')) {
              hasFond = true;
              const credit = parseFloat(c.avance || c.credit || 0) || 0;
              const debit = parseFloat(c.montant || c.debit || 0) || 0;
              totalFond += credit > 0 ? credit : debit;
            }
            if (lib.includes('PAY') && lib.includes('PAR FOND CLIENT')) {
              totalPaye += parseFloat(c.montant || c.debit || 0) || 0;
            }
          });
  if (hasFond) {
            const solde = totalFond - totalPaye;
            setFondClientRecap({
              solde,
              montantFormatted: new Intl.NumberFormat('fr-FR', {
                minimumFractionDigits: 3,
                maximumFractionDigits: 3
              }).format(Math.abs(solde)),
              isNegative: solde < 0,
              isZero: solde <= 0,
            });
          } else {
            setFondClientRecap(null);
          }
        }
      } catch {
        setFondClientRecap(null);
      }

      // Récupérer les charges mensuelles pour l'année sélectionnée
      const chargesRes = await fetch(`/api/charges-mensuelles/client/${id}?annee=${annee}`);
      const chargesData = await chargesRes.json();

      if (chargesData.success) {
        // Trier les charges par date réelle (plus ancien en premier - tri croissant)
        const sortedCharges = chargesData.charges.sort((a, b) => {
          // Utiliser la date réelle si disponible, sinon date_creation
          const dateA = a.date ? new Date(a.date) : (a.date_creation ? new Date(a.date_creation) : new Date(0));
          const dateB = b.date ? new Date(b.date) : (b.date_creation ? new Date(b.date_creation) : new Date(0));

          // Tri croissant : plus ancien en premier
          return dateA - dateB;
        });
        setCharges(sortedCharges);

        // #region agent log
        logEvent({
          hypothesisId: 'H2',
          location: 'ClientCharges.js:fetchClientCharges',
          message: 'Charges fetched',
          data: {
            count: sortedCharges.length,
            sampleIds: sortedCharges.slice(0, 5).map(c => c.id)
          }
        });
        // #endregion

        // Initialiser chargesWithRetraitCgm depuis les données de la base de données
        const retraitProcessedSet = new Set();
        sortedCharges.forEach(charge => {
          if (charge.is_cgm_retrait_processed) {
            retraitProcessedSet.add(charge.id);
          }
        });
        setChargesWithRetraitCgm(retraitProcessedSet);
      } else {
        setCharges([]);
      }
    } catch (err) {
      setError('Erreur lors du chargement des données');
      setHonoRecap(null);
      setFondClientRecap(null);
    } finally {
      setLoading(false);
    }
  };

  const formatMontant = (montant) => {
    const value = parseFloat(montant);
    if (isNaN(value)) return '0,000';
    // Afficher la valeur absolue pour supprimer le signe '-' visuel
    const absValue = Math.abs(value);
    // Afficher avec 3 décimales (ex: 100,000)
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(absValue);
  };

  // Format avec signe '-' si négatif
  const formatMontantWithSign = (montant) => {
    const value = parseFloat(montant || 0);
    const isNeg = value < 0;
    const base = formatMontant(Math.abs(value));
    return `${isNeg ? '-' : ''}${base}`;
  };

  // Affichage formaté par type: montant en vert, avance en rouge, sinon noir
  const renderMontantGreen = (montant) => {
    const num = parseFloat(montant || 0);
    if (Number.isNaN(num) || num === 0) return <span className="text-muted">0,000</span>;
    return <span className="cc-cell-montant">{formatMontant(num)}</span>;
  };

  const renderAvanceRed = (avance) => {
    const num = parseFloat(avance || 0);
    if (Number.isNaN(num) || num === 0) return <span className="text-muted">0,000</span>;
    return <span className="cc-cell-avance">{formatMontant(num)}</span>;
  };

  const renderReste = (montant) => {
    const num = parseFloat(montant || 0);
    const cls = num < 0 ? 'cc-cell-reste-negative' : 'cc-cell-reste-positive';
    return <span className={cls}>{formatMontantWithSign(num)}</span>;
  };

  const getMoisName = (mois) => {
    const moisNames = {
      1: 'JANVIER', 2: 'FEVRIER', 3: 'MARS', 4: 'AVRIL',
      5: 'MAI', 6: 'JUIN', 7: 'JUILLET', 8: 'AOUT',
      9: 'SEPTEMBRE', 10: 'OCTOBRE', 11: 'NOVEMBRE', 12: 'DECEMBRE'
    };
    return moisNames[mois] || mois;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Libellés de base pouvant recevoir un suffixe saisi manuellement
  const baseLibelles = [
    'HONORAIRES REÇU',
    'HONORAIRES PLATEFORME TEJ',
    'HONO',
    'AVANCE',
    'ARGENT CLIENT CHEZ CGM',
    'PAYÉ PAR FOND CLIENT',
    'RC',
    'BENEF.EFFECTIF',
    'RESERVATION',
    'CNSS',
    'DECLARATION',
    'DECLARATION MENSUELLE',
    'ENREGT',
    'RNE',
    'PUBLICATION',
    'CACHET',
  ];

  const isBaseLibelle = (value) => baseLibelles.includes(value);

  // Fonction helper pour vérifier si un libellé est un honoraire reçu (incluant les deux types)
  const isHonoraireRecu = (libelle) => {
    if (!libelle) return false;
    const libelleUpper = libelle.toUpperCase();
    return libelleUpper.includes('HONORAIRES REÇU') ||
      libelleUpper.includes('HONORAIRES RECU') ||
      libelleUpper.includes('HONORAIRES RE') ||
      libelleUpper.includes('HONORAIRES PLATEFORME TEJ') ||
      libelleUpper === 'HONORAIRES PLATEFORME TEJ';
  };

  // Fonction helper pour vérifier si un libellé est une avance (incluant AVANCE et ARGENT CLIENT CHEZ CGM)
  const isAvanceType = (libelle) => {
    if (!libelle) return false;
    const libelleUpper = libelle.toUpperCase();
    return libelleUpper.includes('AVANCE') || libelleUpper.includes('ARGENT CLIENT CHEZ CGM');
  };

  // Fonction pour déterminer si le champ montant doit être bloqué
  const isMontantBlocked = (libelle) => {
    return libelle === 'HONORAIRES REÇU' || libelle === 'HONORAIRES PLATEFORME TEJ' || libelle === 'AVANCE' || libelle === 'ARGENT CLIENT CHEZ CGM';
  };

  // Fonction pour déterminer si le champ avance doit être bloqué
  const isAvanceBlocked = (libelle) => {
    return !isMontantBlocked(libelle) && libelle !== '' && libelle !== 'Sélectionner...';
  };

  const buildLibelleValue = (selected, customText) => {
    if (selected === 'TAPEZ MANUELLE') return customText;
    if (selected === '' || selected === 'Sélectionner...') return selected;
    // Pour tous les autres libellés, ajouter le texte supplémentaire s'il existe
    return `${selected}${customText ? ' ' + customText : ''}`;
  };


  const handleLibelleChange = (e) => {
    const value = e.target.value;
    setFormData(prev => {
      const newFormData = {
        ...prev,
        libelle: value,
        libelleCustom: (value === 'TAPEZ MANUELLE' || (value !== '' && value !== 'Sélectionner...')) ? prev.libelleCustom : ''
      };

      // Bloquer automatiquement les champs selon le libellé sélectionné
      if (isMontantBlocked(value)) {
        // Pour HONORAIRES REÇU et AVANCE : bloquer montant, activer avance
        newFormData.montant = '0'; // Mettre 0 pour éviter les erreurs de validation
        newFormData.avance = prev.avance || '';
      } else if (isAvanceBlocked(value)) {
        // Pour les autres libellés : bloquer avance, activer montant
        newFormData.avance = '0'; // Mettre 0 pour éviter les erreurs de validation
        newFormData.montant = prev.montant || '';
      }

      return newFormData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let finalLibelle = buildLibelleValue(formData.libelle, formData.libelleCustom);
      // Ajouter le préfixe [CARTE BANCAIRE] si c'est une charge carte bancaire
      if (isCarteBancaire) {
        finalLibelle = `[CARTE BANCAIRE] ${finalLibelle}`;
      }
      // Pour le caissier : n'envoyer que l'une des 3 dates autorisées (jour, 01/01, 31/12)
      const dateForApi = isCaissier()
        ? getCaissierDateValueForSelect(formData.date).split('T')[0]
        : (formData.date && formData.date.includes('T') ? formData.date.split('T')[0] : formData.date);
      const response = await fetch('/api/charges-mensuelles', {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify({
          client_id: parseInt(id),
          date: dateForApi,
          libelle: finalLibelle,
          montant: isMontantBlocked(formData.libelle) ? 0 : parseFloat(formData.montant || 0),
          avance: isAvanceBlocked(formData.libelle) ? 0 : parseFloat(formData.avance || 0)
        })
      });

      const result = await response.json();

      if (result.success) {
        const newChargeId = result.charge?.id || result.chargeId;
        const chargeMontant = parseFloat(formData.montant || 0);
        const chargeAvance = parseFloat(formData.avance || 0);
        const isDepense = chargeMontant > 0 && chargeAvance === 0;

        // Pour les honoraires reçus: mettre à jour la caisse uniquement si ce n'est PAS une carte bancaire
        if (!isCarteBancaire && finalLibelle && isHonoraireRecu(finalLibelle)) {
          window.dispatchEvent(new CustomEvent('caisse-updated'));
          window.dispatchEvent(new CustomEvent('charge-added'));

          // Créer une notification pour l'admin si l'utilisateur est un caissier
          if (user && user.role === 'caissier') {
            try {
              // Récupérer le nom d'utilisateur du caissier
              let caissierUsername = user.username || 'Caissier Inconnu';

              // Déterminer le montant : si c'est une avance (montant = 0 et avance > 0), utiliser avance, sinon utiliser montant
              const isAdvance = chargeMontant === 0 && chargeAvance > 0;
              const montant = isAdvance ? chargeAvance : chargeMontant;

              if (montant > 0) {
                // Construire le nom complet du client
                const clientNomComplet = client ? `${client.prenom || ''} ${client.nom || ''}`.trim() : 'Client inconnu';

                // Message de notification en minuscules
                const message = `${caissierUsername} a ajouté une honoraire reçu de montant ${formatMontant(montant)} du client ${clientNomComplet}`.toLowerCase();

                const notifResponse = await fetch('/api/notifications', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    message: message,
                    caissier_username: caissierUsername,
                    client_nom: client?.nom || '',
                    client_prenom: client?.prenom || '',
                    montant: montant,
                    type_notification: 'honoraire_added'
                  })
                });

                if (notifResponse.ok) {
                  console.log('✅ Notification créée pour l\'admin');
                  // Déclencher un événement pour mettre à jour les notifications dans le Header
                  window.dispatchEvent(new CustomEvent('new-notification'));
                }
              }
            } catch (notifError) {
              console.error('Erreur lors de la création de la notification:', notifError);
              // Ne pas bloquer le processus si la notification échoue
            }
          }
        }

        // Pour les charges dépense (montant>0, avance=0): ajouter automatiquement aux dépenses client (y compris carte bancaire)
        if (isDepense && newChargeId) {
          // Si c'est une charge avec montant (dépense), l'ajouter automatiquement aux dépenses client
          try {
            const depenseResponse = await fetch('/api/depenses', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                date: dateForApi || (formData.date ? formData.date.split('T')[0] : new Date().toISOString().slice(0, 10)),
                beneficiaire: client?.nom || 'Client',
                montant: chargeMontant,
                description: finalLibelle || 'Dépense liée à charge',
                type: 'client',
                client_id: parseInt(id),
                charge_id: newChargeId
              })
            });

            const depenseResult = await depenseResponse.json();
            if (depenseResult.success) {
              // Marquer immédiatement cette charge comme ayant une dépense client
              setChargesWithClientExpenses(prev => new Set([...prev, newChargeId]));

              // Créer une notification pour l'admin si c'est une dépense carte bancaire et que l'utilisateur est un caissier
              if (isCarteBancaire && user && user.role === 'caissier' && chargeMontant > 0) {
                try {
                  // Récupérer le nom d'utilisateur du caissier
                  let caissierUsername = user.username || 'Caissier Inconnu';

                  // Construire le nom complet du client
                  const clientNomComplet = client ? `${client.prenom || ''} ${client.nom || ''}`.trim() : 'Client inconnu';

                  // Message de notification avec "a ajouté" en minuscules
                  const message = `${caissierUsername} a ajouté(e) une dépense carte bancaire de montant ${formatMontant(chargeMontant)} du client ${clientNomComplet}`;

                  const notifResponse = await fetch('/api/notifications', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      message: message,
                      caissier_username: caissierUsername,
                      client_nom: client?.nom || '',
                      client_prenom: client?.prenom || '',
                      montant: chargeMontant,
                      type_notification: 'carte_bancaire_added'
                    })
                  });

                  if (notifResponse.ok) {
                    console.log('✅ Notification créée pour l\'admin (dépense carte bancaire)');
                    // Déclencher un événement pour mettre à jour les notifications dans le Header
                    window.dispatchEvent(new CustomEvent('new-notification'));
                  }
                } catch (notifError) {
                  console.error('Erreur lors de la création de la notification (carte bancaire):', notifError);
                  // Ne pas bloquer le processus si la notification échoue
                }
              }
            }
          } catch (err) {
            console.error('Erreur lors de l\'ajout automatique aux dépenses client:', err);
          }
          window.dispatchEvent(new CustomEvent('charge-added'));
        } else {
          // Pour les autres charges, mettre à jour normalement
          window.dispatchEvent(new CustomEvent('charge-added'));
        }

        // Recharger les charges après ajout
        await fetchClientCharges();
        setShowForm(false);
        setFormData({
          date: isCaissier() ? getCaissierDateOptions(annee)[0].value : new Date().toISOString().slice(0, 16),
          libelle: '',
          libelleCustom: '',
          montant: '',
          avance: ''
        });
        // Afficher un message de succès
        if (!isCarteBancaire && finalLibelle && isHonoraireRecu(finalLibelle)) {
          setSuccessMessage('Honoraires reçus ajoutés ! La caisse CGM a été mise à jour.');
        } else if (isDepense) {
          setSuccessMessage('Charge ajoutée et automatiquement enregistrée dans les dépenses client !');
        } else {
          setSuccessMessage('Charge ajoutée avec succès !');
        }
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.error || 'Erreur lors de l\'ajout de la charge');
      }
    } catch (err) {
      setError('Erreur lors de l\'ajout de la charge');
    } finally {
      setLoading(false);
    }
  };

  // Pour le caissier : 3 dates autorisées (date du jour, 01/01 année sélectionnée, 31/12 année précédente → solde reporté)
  const getCaissierDateOptions = (anneeSelectionnee) => {
    const d = new Date();
    const y = anneeSelectionnee != null ? Number(anneeSelectionnee) : d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const todayStr = `${d.getFullYear()}-${m}-${day}`;
    return [
      { label: `${day}/${m}/${d.getFullYear()}`, value: `${todayStr}T08:00` },
      { label: `01/01/${y}`, value: `${y}-01-01T08:00` },
      { label: `31/12/${y - 1}`, value: `${y - 1}-12-31T08:00` }
    ];
  };

  // Pour le caissier : ramener une date du formulaire à la valeur affichée (une des 3 options)
  const getCaissierDateValueForSelect = (dateStr) => {
    if (!dateStr) return getCaissierDateOptions(annee)[0].value;
    const part = dateStr.split('T')[0];
    const opts = getCaissierDateOptions(annee);
    const found = opts.find(o => o.value.startsWith(part));
    return found ? found.value : opts[0].value;
  };

  // Pour le caissier en édition : choisir laquelle des 3 dates correspond à la charge
  const getCaissierDateForCharge = (chargeDateInput) => {
    const opts = getCaissierDateOptions(annee);
    if (!chargeDateInput) return opts[0].value;
    const chargePart = (chargeDateInput.match(/^\d{4}-\d{2}-\d{2}/) || [])[0] ||
      (() => { const d = new Date(chargeDateInput); return isNaN(d.getTime()) ? null : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
    if (!chargePart) return opts[0].value;
    const found = opts.find(o => o.value.startsWith(chargePart));
    return found ? found.value : opts[0].value;
  };

  // Fonction pour formater une date pour l'affichage sans problème de fuseau horaire
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '-';

    // Si c'est une date seule (YYYY-MM-DD), extraire directement les composants
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }

    // Horodatage ISO / SQL : jour calendrier local (évite décalage UTC)
    if (dateString.match(/^\d{4}-\d{2}-\d{2}[T\s]/)) {
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${day}/${month}/${year}`;
        }
      } catch (_) { /* fallthrough */ }
    }

    // Sinon, essayer de parser avec Date mais utiliser les méthodes locales
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    } catch (e) {
      return '-';
    }
  };

  // Fonction pour formater une date pour datetime-local sans problème de fuseau horaire
  const formatDateForInput = (dateInput) => {
    // Si c'est un objet Date, extraire directement les composants locaux
    if (dateInput instanceof Date) {
      if (isNaN(dateInput.getTime())) {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      }
      const year = dateInput.getFullYear();
      const month = String(dateInput.getMonth() + 1).padStart(2, '0');
      const day = String(dateInput.getDate()).padStart(2, '0');
      const hours = String(dateInput.getHours()).padStart(2, '0');
      const minutes = String(dateInput.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    if (!dateInput) {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    // Convertir en string si ce n'est pas déjà le cas
    const dateString = String(dateInput);

    // Si la date est déjà au format datetime-local
    if (dateString.includes('T') && dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/)) {
      return dateString.slice(0, 16);
    }

    // Si c'est une date seule (YYYY-MM-DD) - IMPORTANT: extraire directement sans new Date()
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Extraire directement les composants sans passer par Date() pour éviter les problèmes de fuseau horaire
      const [year, month, day] = dateString.split('-');
      // Utiliser l'heure actuelle pour préserver l'heure si elle existait
      return `${year}-${month}-${day}T00:00`;
    }

    // Si c'est une date avec heure (YYYY-MM-DD HH:MM:SS ou YYYY-MM-DD HH:MM:SS.mmm)
    if (dateString.match(/^\d{4}-\d{2}-\d{2}[\sT]\d{2}:\d{2}/)) {
      const dateTimeMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})[\sT](\d{2}:\d{2})/);
      if (dateTimeMatch) {
        return `${dateTimeMatch[1]}T${dateTimeMatch[2]}`;
      }
    }

    // Si c'est une date au format ISO avec timezone (YYYY-MM-DDTHH:MM:SSZ ou YYYY-MM-DDTHH:MM:SS+HH:MM)
    if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
      // Extraire seulement la date et l'heure sans la timezone
      const dateTimeMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
      if (dateTimeMatch) {
        return `${dateTimeMatch[1]}T${dateTimeMatch[2]}`;
      }
    }

    // Dernier recours: essayer de parser mais utiliser les méthodes locales
    // ATTENTION: new Date() peut causer un décalage d'un jour selon le fuseau horaire
    // On essaie d'abord d'extraire manuellement les composants
    try {
      // Essayer d'extraire les composants manuellement si possible
      const dateMatch = dateString.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const [, year, month, day] = dateMatch;
        // Essayer d'extraire l'heure si elle existe
        const timeMatch = dateString.match(/\d{2}:\d{2}/);
        if (timeMatch) {
          return `${year}-${month}-${day}T${timeMatch[0]}`;
        }
        return `${year}-${month}-${day}T00:00`;
      }

      // Si on ne peut pas extraire manuellement, utiliser new Date() mais avec les méthodes locales
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        // Si le parsing échoue, retourner la date actuelle
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      }
      // Utiliser les méthodes locales pour éviter les problèmes de fuseau horaire
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (e) {
      // En cas d'erreur, retourner la date actuelle
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }
  };

  const handleEditCharge = (charge) => {
    if (!canEditOperations) return;
    setEditingCharge(charge);
    setShowEditForm(true);
    setShowForm(false);
  };

  const handleUpdateCharge = async (e) => {
    e.preventDefault();
    try {
      // Extraire seulement la partie date (YYYY-MM-DD) ; pour le caissier, n'envoyer qu'une des 3 dates autorisées
      let dateToSend = isCaissier()
        ? getCaissierDateValueForSelect(formData.date).split('T')[0]
        : formData.date;
      if (dateToSend && dateToSend.includes('T')) {
        // Si c'est au format datetime-local (YYYY-MM-DDTHH:MM), extraire seulement la date
        dateToSend = dateToSend.split('T')[0];
      } else if (dateToSend && dateToSend.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Si c'est déjà au format YYYY-MM-DD, l'utiliser tel quel
        dateToSend = dateToSend;
      } else {
        // Sinon, essayer de formater depuis une date
        const dateObj = new Date(dateToSend);
        if (!isNaN(dateObj.getTime())) {
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          dateToSend = `${year}-${month}-${day}`;
        } else {
          // En cas d'erreur, utiliser la date actuelle
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          dateToSend = `${year}-${month}-${day}`;
        }
      }

      const response = await fetch(`/api/charges-mensuelles/${editingCharge.id}`, {
        method: 'PUT',
        headers: authJsonHeaders(),
        body: JSON.stringify({
          ...formData,
          date: dateToSend, // Utiliser la date formatée sans l'heure
          libelle: buildLibelleValue(formData.libelle, formData.libelleCustom),
          montant: isMontantBlocked(formData.libelle) ? 0 : parseFloat(formData.montant || 0),
          avance: isAvanceBlocked(formData.libelle) ? 0 : parseFloat(formData.avance || 0)
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccessMessage('Charge modifiée avec succès !');
        setShowEditForm(false);
        setEditingCharge(null);
        setFormData({ date: new Date().toISOString().slice(0, 16), libelle: '', libelleCustom: '', montant: '', avance: 0 });
        fetchClientCharges();

        // Mettre à jour la caisse si c'est un honoraire reçu
        const finalLibelleEdit = buildLibelleValue(formData.libelle, formData.libelleCustom);
        if (finalLibelleEdit && isHonoraireRecu(finalLibelleEdit)) {
          window.dispatchEvent(new CustomEvent('caisse-updated'));
          window.dispatchEvent(new CustomEvent('charge-updated'));
        }

        // Déclencher un événement pour rafraîchir l'état client
        // Cela permet de mettre à jour l'état automatiquement après modification d'une charge
        window.dispatchEvent(new CustomEvent('charge-updated'));
        window.dispatchEvent(new CustomEvent('etat-client-updated', {
          detail: { clientId: id }
        }));

        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(data.error || 'Erreur lors de la modification de la charge.');
      }
    } catch (err) {
      console.error('Erreur lors de la modification de la charge:', err);
      setError('Erreur lors de la modification de la charge.');
    }
  };

  const handleDeleteCharge = async (chargeId) => {
    if (!canEditOperations) return;
    if (window.confirm('Êtes-vous sûr de supprimer ça ?')) {
      // #region agent log
      logEvent({
        hypothesisId: 'H1',
        location: 'ClientCharges.js:handleDeleteCharge:start',
        message: 'Delete requested',
        data: { chargeId, chargesCount: charges.length }
      });
      // #endregion
      try {
        const response = await fetch(`/api/charges-mensuelles/${chargeId}`, {
          method: 'DELETE',
          headers: authJsonHeaders(),
        });

        const data = await response.json();

        if (data.success) {
          // #region agent log
          logEvent({
            hypothesisId: 'H2',
            location: 'ClientCharges.js:handleDeleteCharge:success',
            message: 'Backend delete success',
            data: { chargeId, success: data.success }
          });
          // #endregion
          const deletedCharge = charges.find(c => c.id === chargeId);

          // Si la charge supprimée est "PAYÉ PAR FOND CLIENT", supprimer aussi le dépôt correspondant dans la caisse CGM
          if (deletedCharge && deletedCharge.libelle) {
            const lib = (deletedCharge.libelle || '').toUpperCase();
            const isPayeParFondClient = lib.includes('PAYE PAR FOND CLIENT') || lib.includes('PAYÉ PAR FOND CLIENT');
            if (isPayeParFondClient) {
              try {
                const chargeDate = deletedCharge.date ? deletedCharge.date.split('T')[0] : null;
                const montantDepot = (() => {
                  const av = parseFloat(deletedCharge.avance || 0) || 0;
                  const mt = parseFloat(deletedCharge.montant || 0) || 0;
                  return Math.max(av, mt);
                })();

                if (montantDepot > 0 && chargeDate) {
                  // Chercher les dépôts de la date concernée
                  const depotsRes = await fetch(`/api/caisse-cgm/depots?date=${chargeDate}`);
                  const depotsData = await depotsRes.json();
                  if (depotsData.success && Array.isArray(depotsData.depots)) {
                    const clientNameUpper = (client?.nom || '').toUpperCase();
                    const matchingDepot = depotsData.depots.find(dep => {
                      const depComment = (dep.commentaire || '').toUpperCase();
                      const depMontant = parseFloat(dep.montant || 0);
                      const sameMontant = Math.abs(depMontant - montantDepot) < 0.01;
                      const hasClient = clientNameUpper && depComment.includes(clientNameUpper);
                      const hasLibelle = depComment.includes('PAYE PAR FOND CLIENT') || depComment.includes('PAYÉ PAR FOND CLIENT');
                      return sameMontant && hasClient && hasLibelle;
                    });

                    if (matchingDepot && matchingDepot.id) {
                      try {
                        await fetch(`/api/caisse-cgm/${matchingDepot.id}`, { method: 'DELETE' });
                      } catch (delErr) {
                        console.error('Erreur lors de la suppression du dépôt caisse CGM lié au fond client :', delErr);
                      }
                    }
                  }
                }
              } catch (err) {
                console.error('Erreur lors de la synchronisation suppression fond client / caisse CGM :', err);
              }
            }
          }

          setSuccessMessage('Charge supprimée avec succès !');
          fetchClientCharges();
          // Mettre à jour la caisse en direct
          window.dispatchEvent(new CustomEvent('caisse-updated'));
          window.dispatchEvent(new CustomEvent('charge-deleted'));
          setTimeout(() => setSuccessMessage(''), 3000);
        } else {
          setErrorMessage(data.error || data.message || 'Erreur lors de la suppression de la charge.');
          setTimeout(() => setErrorMessage(''), 5000);
        }
      } catch (err) {
        console.error('Erreur lors de la suppression de la charge:', err);
        setErrorMessage('Erreur lors de la suppression de la charge.');
        setTimeout(() => setErrorMessage(''), 5000);
      }
    }
  };

  // Note: Tous les boutons (Client, CGM, Client et CGM) sont maintenant affichés pour chaque charge

  const handleClientClick = async (charge) => {
    // Empêcher les double-clics et les actions multiples
    if (chargesWithClientExpenses.has(charge.id)) {
      return;
    }

    try {
      // Utiliser l'endpoint /api/depenses avec type: 'client' pour stocker dans depenses_client
      const requestData = {
        date: charge.date ? charge.date.split('T')[0] : new Date().toISOString().slice(0, 10),
        beneficiaire: client?.nom || 'Client',
        montant: (() => {
          const chargeLibelle = (charge.libelle || '').toUpperCase();
          if (isHonoraireRecu(charge.libelle) || isAvanceType(charge.libelle)) {
            return parseFloat(charge.avance || 0);
          }
          // Pour les dépenses (montant > 0, avance = 0), utiliser le montant complet
          return parseFloat(charge.montant || 0);
        })(),
        description: charge.libelle || 'Dépense liée à charge',
        type: 'client', // Paramètre pour indiquer que c'est pour depenses_client
        client_id: parseInt(id), // ID du client
        charge_id: charge.id // ID de la charge
      };

      // Marquer immédiatement comme en cours pour éviter les doubles clics
      setChargesWithClientExpenses(prev => new Set([...prev, charge.id]));

      const response = await fetch('/api/depenses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        // En cas d'erreur, retirer le marqueur
        setChargesWithClientExpenses(prev => {
          const newSet = new Set(prev);
          newSet.delete(charge.id);
          return newSet;
        });
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setSuccessMessage('Dépense ajoutée aux dépenses client');
        setTimeout(() => setSuccessMessage(''), 3000);
        // Recharger les charges pour mettre à jour l'affichage
        await fetchClientCharges();
      } else {
        // En cas d'échec, retirer le marqueur
        setChargesWithClientExpenses(prev => {
          const newSet = new Set(prev);
          newSet.delete(charge.id);
          return newSet;
        });
        setErrorMessage(result.error || 'Erreur lors de l\'ajout de la dépense');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    } catch (e) {
      // En cas d'erreur, retirer le marqueur
      setChargesWithClientExpenses(prev => {
        const newSet = new Set(prev);
        newSet.delete(charge.id);
        return newSet;
      });
      setErrorMessage(`Erreur: ${e.message}`);
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleCgmClick = async (charge) => {
    // Empêcher les double-clics et les actions multiples
    if (chargesWithCgmExpenses.has(charge.id)) {
      return;
    }

    try {
      // Marquer immédiatement comme en cours pour éviter les doubles clics
      setChargesWithCgmExpenses(prev => new Set([...prev, charge.id]));

      const response = await fetch('/api/depenses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: charge.date ? charge.date.split('T')[0] : new Date().toISOString().slice(0, 10),
          beneficiaire: client?.nom || 'Client',
          montant: (() => {
            const chargeLibelle = (charge.libelle || '').toUpperCase();
            if (chargeLibelle.includes('HONORAIRES REÇU') || isAvanceType(charge.libelle)) {
              return parseFloat(charge.avance || 0);
            }
            // Pour les dépenses (montant > 0, avance = 0), utiliser le montant complet
            return parseFloat(charge.montant || 0);
          })(),
          description: `[CGM] ${charge.libelle || 'Dépense liée à charge'}`,
          type: 'bureau' // Pour stocker dans beneficiaires_bureau
        })
      });

      const result = await response.json();
      if (result.success) {
        setSuccessMessage('Dépense ajoutée aux dépenses CGM');
        setTimeout(() => setSuccessMessage(''), 3000);
        // Mettre à jour la caisse live (car les dépenses CGM diminuent la caisse)
        try { window.dispatchEvent(new CustomEvent('caisse-updated')); } catch (_) { }
        // Recharger les charges pour mettre à jour l'affichage
        await fetchClientCharges();
      } else {
        // En cas d'échec, retirer le marqueur
        setChargesWithCgmExpenses(prev => {
          const newSet = new Set(prev);
          newSet.delete(charge.id);
          return newSet;
        });
        setErrorMessage(result.error || 'Erreur lors de l\'ajout de la dépense');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    } catch (e) {
      // En cas d'erreur, retirer le marqueur
      setChargesWithCgmExpenses(prev => {
        const newSet = new Set(prev);
        newSet.delete(charge.id);
        return newSet;
      });
      setErrorMessage('Erreur réseau lors de l\'ajout de la dépense');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  // Fonction pour créer un retrait direct dans la caisse CGM
  const handleRetraitCaisseCgm = async (charge) => {
    try {
      // Vérifier si le bouton a déjà été cliqué pour cette charge (depuis la base de données)
      if (charge.is_cgm_retrait_processed || chargesWithRetraitCgm.has(charge.id)) {
        return; // Ne rien faire si déjà traité
      }

      const response = await fetch('/api/caisse-cgm/retrait', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          montant: parseFloat(charge.montant || 0),
          commentaire: `${client?.nom || 'Client'} - ${charge.libelle || 'Dépense'}`,
          note: `${client?.nom || 'Client'} - ${charge.libelle || 'Dépense'}`,
          type_retrait: 'autre',
          is_bureau: 0,
        })
      });

      const result = await response.json();
      if (result.success) {
        // Marquer cette charge comme ayant eu son bouton '-' cliqué (mise à jour locale immédiate)
        setChargesWithRetraitCgm(prev => new Set([...prev, charge.id]));
        setSuccessMessage('Retrait créé dans la caisse CGM');
        setTimeout(() => setSuccessMessage(''), 3000);
        // Mettre à jour la caisse live
        try { window.dispatchEvent(new CustomEvent('caisse-updated')); } catch (_) { }
        // Recharger les charges pour mettre à jour l'affichage (la base de données sera à jour)
        await fetchClientCharges();
      } else {
        setErrorMessage(result.error || 'Erreur lors de la création du retrait');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    } catch (e) {
      setErrorMessage('Erreur réseau lors de la création du retrait');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  // Créer un dépôt dans la caisse CGM pour une charge "PAYÉ PAR FOND CLIENT"
  const handleDepotFondClient = async (charge) => {
    try {
      if (chargesWithFondClientDepot.has(charge.id)) {
        return;
      }

      setChargesWithFondClientDepot(prev => new Set([...prev, charge.id]));

      const montantRawAvance = parseFloat(charge.avance || 0) || 0;
      const montantRawMontant = parseFloat(charge.montant || 0) || 0;
      const montantDepot = Math.max(montantRawAvance, montantRawMontant);

      if (!montantDepot || montantDepot <= 0) {
        setChargesWithFondClientDepot(prev => {
          const next = new Set(prev);
          next.delete(charge.id);
          return next;
        });
        setErrorMessage('Montant invalide pour le dépôt fond client');
        setTimeout(() => setErrorMessage(''), 3000);
        return;
      }

      const response = await fetch('/api/caisse-cgm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type_operation: 'depot',
          montant: montantDepot,
          commentaire: `${client?.nom || 'Client'} - ${charge.libelle || 'Dépôt fond client'}`,
          client_id: parseInt(id),
          operation_sign: 'plus'
        })
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.success) {
        throw new Error(
          result.error ||
          (response.status === 410
            ? 'Les dépôts caisse legacy ne sont plus supportés'
            : `HTTP error! status: ${response.status}`)
        );
      }

      // Ici on ne supprime plus la charge : elle reste visible dans les charges client / fond client / états.
      // On considère uniquement que le montant a été déposé en caisse CGM.

      setSuccessMessage('Dépôt créé dans la caisse CGM depuis le fond client (charge conservée)');
      setTimeout(() => setSuccessMessage(''), 3000);
      try { window.dispatchEvent(new CustomEvent('caisse-updated')); } catch (_) { }
    } catch (e) {
      setChargesWithFondClientDepot(prev => {
        const next = new Set(prev);
        next.delete(charge.id);
        return next;
      });
      setErrorMessage(`Dépôt caisse indisponible: ${e.message}`);
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  // Carte bancaire -> restauration argent: retrait fond client + dépôt caisse live
  const handleBanqueClick = async (charge) => {
    try {
      if (chargesWithBanqueRestauree.has(charge.id)) return;

      setChargesWithBanqueRestauree(prev => new Set([...prev, charge.id]));

      const restorationRes = await fetch('/api/charges-mensuelles/restauration-banque', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: parseInt(id, 10),
          charge_id: charge.id
        })
      });
      const restorationData = await restorationRes.json();

      if (restorationRes.status === 409) {
        // Déjà restauré — garder le bouton gris silencieusement
        return;
      }

      if (!restorationRes.ok || !restorationData.success) {
        throw new Error(restorationData.error || restorationData.message || 'Échec restauration argent');
      }

      setSuccessMessage('Restauration argent effectuée: fond client -> caisse live.');
      setTimeout(() => setSuccessMessage(''), 3500);
      try { window.dispatchEvent(new CustomEvent('caisse-updated')); } catch (_) { }
      await fetchClientCharges();
    } catch (e) {
      setErrorMessage(`Erreur restauration argent: ${e.message}`);
      setTimeout(() => setErrorMessage(''), 4000);
      setChargesWithBanqueRestauree(prev => {
        const next = new Set(prev);
        next.delete(charge.id);
        return next;
      });
    }
  };

  const handleClientEtCgmClick = async (charge) => {
    try {
      // Ajouter aux dépenses client (dans depenses_client)
      const clientResponse = await fetch('/api/depenses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: charge.date ? charge.date.split('T')[0] : new Date().toISOString().slice(0, 10),
          beneficiaire: client?.nom || 'Client',
          montant: (() => {
            const chargeLibelle = (charge.libelle || '').toUpperCase();
            if (chargeLibelle.includes('HONORAIRES REÇU') || isAvanceType(charge.libelle)) {
              return parseFloat(charge.avance || 0);
            }
            return Math.abs(parseFloat(charge.montant) - parseFloat(charge.avance || 0)) || 0;
          })(),
          description: charge.libelle || 'Dépense liée à charge',
          type: 'client', // Pour stocker dans depenses_client
          client_id: parseInt(id), // ID du client
          charge_id: charge.id // ID de la charge
        })
      });

      // Ajouter aux dépenses CGM (dans beneficiaires_bureau)
      const cgmResponse = await fetch('/api/depenses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: charge.date ? charge.date.split('T')[0] : new Date().toISOString().slice(0, 10),
          beneficiaire: client?.nom || 'Client',
          montant: (() => {
            const chargeLibelle = (charge.libelle || '').toUpperCase();
            if (chargeLibelle.includes('HONORAIRES REÇU') || isAvanceType(charge.libelle)) {
              return parseFloat(charge.avance || 0);
            }
            return Math.abs(parseFloat(charge.montant) - parseFloat(charge.avance || 0)) || 0;
          })(),
          description: `[CGM] ${charge.libelle || 'Dépense liée à charge'}`,
          type: 'bureau' // Pour stocker dans beneficiaires_bureau
        })
      });

      const clientResult = await clientResponse.json();
      const cgmResult = await cgmResponse.json();

      if (clientResult.success && cgmResult.success) {
        setSuccessMessage('Dépense ajoutée aux dépenses client et CGM');
        setTimeout(() => setSuccessMessage(''), 3000);
        // Rafraîchir la caisse live immédiatement
        try { window.dispatchEvent(new CustomEvent('caisse-updated')); } catch (_) { }
        // Marquer immédiatement cette charge comme ayant une dépense client et CGM
        setChargesWithClientAndCgmExpenses(prev => new Set([...prev, charge.id]));
        fetchClientCharges();
      } else {
        setErrorMessage('Erreur lors de l\'ajout de la dépense');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    } catch (e) {
      setErrorMessage('Erreur réseau lors de l\'ajout de la dépense');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  // Ouvrir le gabarit de reçu avec les données de la charge (opérations crédit)
  const handlePrintCharge = async (charge) => {
    try {
      const isCredit =
        charge.operation_side === 'credit' ||
        parseFloat(charge.avance || 0) > 0 ||
        isHonoraireRecu(charge.libelle) ||
        isAvanceType(charge.libelle);

      if (!isCredit) {
        console.warn('Le reçu est réservé aux opérations crédit');
        return;
      }

      console.log('Clic sur Reçu - Enregistrement dans l\'historique...');
      await recordPrintHistory(charge, client);
      console.log('Enregistrement terminé depuis ClientCharges');

      navigate(`/client/${id}/print-receipt?chargeId=${charge.id}`);
    } catch (err) {
      console.error('Erreur ouverture reçu:', err);
      navigate(`/client/${id}/print-receipt?chargeId=${charge.id}`);
    }
  };

  // Enregistrer l'impression dans l'historique
  const recordPrintHistory = async (charge, client) => {
    try {
      // Vérifier que le client est disponible
      if (!client || !client.id) {
        console.error('Client non disponible pour l\'enregistrement de l\'impression');
        return;
      }

      // Récupérer le nom d'utilisateur du caissier depuis useAuth ou sessionStorage
      let caissierUsername = 'Caissier Inconnu';
      try {
        // Essayer d'abord avec useAuth
        if (user && user.username) {
          caissierUsername = user.username;
        } else {
          // Sinon, essayer avec sessionStorage
          const userStr = sessionStorage.getItem('user');
          if (userStr) {
            const userFromStorage = JSON.parse(userStr);
            if (userFromStorage && userFromStorage.username) {
              caissierUsername = userFromStorage.username;
            }
          }
        }
      } catch (e) {
        console.warn('Erreur lors de la récupération du nom d\'utilisateur:', e);
      }

      // Déterminer le montant : crédit (avance) en priorité pour les ops crédit
      const chargeLibelle = (charge.libelle || '').toUpperCase();
      const montantDebit = parseFloat(charge.montant || 0) || 0;
      const montantCredit = parseFloat(charge.avance || 0) || 0;
      const isAvance = isAvanceType(charge.libelle) || (montantDebit === 0 && montantCredit > 0);
      const isCredit =
        charge.operation_side === 'credit' ||
        montantCredit > 0 ||
        isHonoraireRecu(charge.libelle) ||
        isAvance;
      const montant = isCredit
        ? Math.max(montantCredit, montantDebit)
        : Math.max(montantDebit, montantCredit);

      if (isNaN(montant) || montant <= 0) {
        console.warn('Montant invalide pour l\'enregistrement de l\'impression:', montant);
        return;
      }

      const printData = {
        client_id: client.id,
        client_nom: client.nom || client.username || client.name || 'Client',
        client_prenom: client.prenom || '',
        montant: montant,
        caissier_username: caissierUsername,
        type_reçu: isAvance ? 'avance' : 'honoraires',
        charge_id: charge.id,
        libelle: charge.libelle || '',
      };

      // Envoyer les données au backend
      const response = await fetch('/api/print-history', {
        method: 'POST',
        headers: authJsonHeaders(),
        body: JSON.stringify(printData)
      });

      const data = await response.json();

      console.log('Réponse API print-history depuis ClientCharges:', { status: response.status, ok: response.ok, data });

      if (response.ok && data.success) {
        console.log('✅ Reçu enregistré avec succès dans l\'historique depuis ClientCharges:', printData);
        // Afficher un message de succès
        setSuccessMessage('Reçu enregistré dans l\'historique avec succès');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const errorMsg = data.error || data.message || response.statusText || 'Erreur lors de l\'enregistrement';
        console.error('❌ Erreur lors de l\'enregistrement de l\'impression:', errorMsg, printData);
        setErrorMessage(`Erreur: ${errorMsg}`);
        setTimeout(() => setErrorMessage(''), 5000);
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de l\'impression:', error);
      throw error; // Re-lancer l'erreur pour que handlePrintCharge puisse la gérer
    }
  };

  if (loading) {
    return (
      <div className="client-charges-page">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="client-charges-page">
        <div className="cc-alert-danger" role="alert">{error}</div>
        <button type="button" className="cgm-toolbar-btn cgm-toolbar-btn--back cc-back-btn" onClick={() => navigate('/clients')}>
          <i className="fas fa-arrow-left" aria-hidden="true" /> Retour
        </button>
      </div>
    );
  }

  // Filtrer les charges selon le libellé sélectionné
  const filteredCharges = (filterLibelle === ''
    ? charges
    : charges.filter(charge => {
      const chargeLibelle = (charge.libelle || '').toUpperCase();
      const filterLibelleUpper = filterLibelle.toUpperCase();
      // Vérifier si le libellé de la charge commence par le libellé filtré
      return chargeLibelle.startsWith(filterLibelleUpper) || chargeLibelle.includes(filterLibelleUpper);
    })
  );

  const totalFilteredCharges = filteredCharges.length;
  const totalPages = Math.max(1, Math.ceil(totalFilteredCharges / itemsPerPage));
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const pagedCharges = filteredCharges.slice(indexOfFirstItem, indexOfLastItem);

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const goToPrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  return (
    <div className="client-charges-page">
      <style jsx global>{`
        .client-charges-page { 
          background: #f1f5f9;
          min-height: 100%;
          padding: 0.75rem 0.35rem 1.25rem;
          position: relative;
          font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
          max-width: none;
          width: 100%;
          margin: 0;
        }
        
        .client-charges-page::before { display: none; }

        .client-charges-page .go-back-button {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.45rem 1rem;
          margin: 0 0 0.75rem 0;
          background: #dc3545;
          color: #ffffff;
          border: 1px solid #c82333;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.18s ease;
          box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
        }

        .client-charges-page .go-back-button:hover {
          background: #c82333;
          color: #ffffff;
          border-color: #bd2130;
          box-shadow: 0 4px 12px rgba(220, 53, 69, 0.35);
        }

        .client-charges-page .charge-hono-auto-badge {
          display: inline-block;
          margin-left: 0.4rem;
          padding: 0.1rem 0.4rem;
          font-size: 0.62rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #1e40af;
          background: #dbeafe;
          border: 1px solid #93c5fd;
          border-radius: 4px;
          vertical-align: middle;
        }

        .client-charges-page .client-charges-top-bar {
          display: flex;
          align-items: center;
          gap: 0.65rem;
          flex-wrap: wrap;
          margin-bottom: 0.75rem;
          width: 100%;
        }

        .client-charges-page .client-charges-top-bar .go-back-button {
          position: static;
          left: auto;
          bottom: auto;
          margin-bottom: 0;
          flex-shrink: 0;
        }

        .client-charges-page .client-hono-recap-bar {
          flex: 1 1 auto;
          min-width: 200px;
          background: #ffffff;
          border-radius: 8px;
          padding: 0.45rem 0.75rem;
          border: 1px solid #bfdbfe;
          box-shadow: 0 2px 10px rgba(37, 99, 235, 0.1);
          border-left: 4px solid #2563eb;
        }

        .client-charges-page .client-hono-recap-bar .client-hono-recap-text {
          color: #475569;
          font-size: 0.8rem;
          line-height: 1.35;
          text-transform: none;
        }

        .client-charges-page .client-hono-recap-bar .client-hono-recap-text strong {
          color: #1e40af;
          font-size: 0.82rem;
        }
        
        
        .charges-header { 
          background: #ffffff;
          border-radius: 12px; 
          padding: 1.25rem 1.5rem; 
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
          margin-bottom: 1.25rem;
          position: relative;
          overflow: hidden;
          border: 1px solid #d5dbe3;
          animation: slideInDown 0.8s ease-out;
          max-width: none;
          width: 100%;
          margin-left: 0;
          margin-right: 0;
        }
        
        .charges-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: #0b5796;
        }
        
        @keyframes slideInDown {
          from { transform: translateY(-50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        
        .charges-title { 
          background: linear-gradient(45deg, #0b5796, #0d6efd);
          -webkit-background-clip: text; 
          -webkit-text-fill-color: transparent; 
          background-clip: text; 
          font-size: 2.4rem; 
          font-weight: 900; 
          margin-bottom: 0.75rem; 
          text-shadow: none;
          position: relative;
        }
        
        @keyframes titleGlow {
          from { filter: drop-shadow(0 0 5px rgba(102, 126, 234, 0.3)); }
          to { filter: drop-shadow(0 0 20px rgba(118, 75, 162, 0.5)); }
        }
        
        .charges-subtitle { 
          color: #4a5568; 
          font-size: 1rem; 
          font-weight: 600; 
          margin-bottom: 1rem;
          opacity: 0.9;
        }
        
        .modern-back-btn { 
          background: linear-gradient(45deg, #667eea, #764ba2);
          border: none; 
          color: white; 
          border-radius: 15px; 
          padding: 12px 24px; 
          font-weight: 600; 
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); 
          text-decoration: none; 
          display: inline-flex; 
          align-items: center; 
          gap: 10px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
        }
        
        .modern-back-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.6s;
        }
        
        .modern-back-btn:hover::before {
          left: 100%;
        }
        
        .modern-back-btn:hover { 
          background: linear-gradient(45deg, #764ba2, #667eea);
          transform: translateY(-3px) scale(1.05); 
          box-shadow: 0 15px 30px rgba(102, 126, 234, 0.4); 
          color: white; 
          text-decoration: none; 
        }
        
        .client-info-card { 
          background: linear-gradient(90deg, #1e3a8a 0%, #2563eb 50%, #3b82f6 100%);
          border-radius: 12px; 
          padding: 0.75rem 1rem; 
          margin-bottom: 0.75rem; 
          border: 1px solid #93c5fd;
          box-shadow: 0 8px 24px rgba(37, 99, 235, 0.2);
          max-width: 1100px;
          margin-left: auto;
          margin-right: auto;
          position: relative;
          overflow: hidden;
        }
        
        .client-info-card::before {
          display: none;
        }
        
        @keyframes slideInUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        .client-info-summary {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          flex-wrap: wrap;
        }
        
        .client-info-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          flex: 0 0 auto;
        }
        
        .client-info-text {
          color: #ffffff;
          font-weight: 600;
          font-size: 0.82rem;
          display: flex;
          align-items: center;
        }
        
        .client-info-text i {
          color: #bfdbfe;
          margin-right: 0.3rem;
          font-size: 0.75rem;
        }
        
        .client-info-card .client-info-year label {
          color: #dbeafe !important;
        }
        
        .client-info-card .form-select {
          border: 1px solid #93c5fd !important;
          border-radius: 6px !important;
          background: #ffffff !important;
          font-size: 0.78rem !important;
        }
        
        .client-username {
          color: #6c757d;
          font-weight: 500;
          font-size: 0.75rem;
          margin-left: 0.3rem;
        }
        
        .client-info-year {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
          flex: 0 0 auto;
        }

        .client-info-filter {
          display: flex;
          align-items: center;
          gap: 0.35rem;
        }

        .client-info-filter label {
          margin-bottom: 0;
          color: #dbeafe;
          font-weight: 600;
          font-size: 0.75rem;
          white-space: nowrap;
        }

        .client-info-filter .form-select {
          min-width: 70px;
          font-size: 0.75rem !important;
          font-weight: 600;
          padding: 2px 6px !important;
        }

        .client-info-filter:last-of-type .form-select {
          min-width: 120px;
        }
        
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(5px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }
        
        .add-charge-form-container {
          background: #ffffff;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(213, 219, 227, 0.8);
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          animation: modalSlideIn 0.3s ease-out;
        }
        
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .add-charge-form-header {
          background: linear-gradient(135deg, #0b5796 0%, #0d6efd 100%);
          color: #ffffff;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 1rem;
          margin-bottom: 1rem;
          text-align: center;
        }
        
        .add-charge-form {
          padding: 0;
        }
        
        .add-charge-form-row {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
          margin-bottom: 1rem;
        }
        
        .add-charge-form-field {
          flex: 1;
          min-width: 150px;
        }
        
        .add-charge-form-label {
          display: block;
          color: #2c3e50;
          font-weight: 500;
          font-size: 0.85rem;
          margin-bottom: 0.4rem;
        }
        
        .add-charge-form-input,
        .add-charge-form-select {
          width: 100%;
          border: 1px solid #d5dbe3;
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 0.9rem;
          transition: all 0.2s ease;
          background: #ffffff;
        }
        
        .add-charge-form-input:focus,
        .add-charge-form-select:focus {
          outline: none;
          border-color: #0b5796;
          box-shadow: 0 0 0 2px rgba(11, 87, 150, 0.12);
        }
        
        .add-charge-form-input-disabled {
          background: #f4f6f8;
          color: #6c757d;
          cursor: not-allowed;
        }
        
        .add-charge-form-input-custom {
          margin-top: 0.3rem;
        }
        
        .add-charge-form-actions {
          display: flex;
          gap: 0.75rem;
          justify-content: center;
          margin-top: 1.5rem;
        }
        
        .add-charge-form-btn-submit {
          background: #2E7D32;
          border: none;
          color: white;
          border-radius: 6px;
          padding: 10px 24px;
          font-weight: 500;
          font-size: 0.95rem;
          transition: all 0.2s ease;
          cursor: pointer;
          flex: 1;
          max-width: 200px;
        }
        
        .add-charge-form-btn-submit:hover:not(:disabled) {
          background: #256528;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(46, 125, 50, 0.3);
        }
        
        .add-charge-form-btn-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }
        
        .add-charge-form-btn-cancel {
          background: #6c757d;
          border: none;
          color: white;
          border-radius: 6px;
          padding: 10px 24px;
          font-weight: 500;
          font-size: 0.95rem;
          transition: all 0.2s ease;
          cursor: pointer;
          flex: 1;
          max-width: 200px;
        }
        
        .add-charge-form-btn-cancel:hover {
          background: #5a6268;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
        }
        
        .add-charge-btn {
          background: linear-gradient(180deg, #3b82f6 0%, #2563eb 100%);
          border: 1px solid #1d4ed8;
          color: #ffffff;
          border-radius: 8px;
          padding: 0.4rem 0.75rem;
          font-weight: 600;
          font-size: 0.78rem;
          transition: all 0.18s ease;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          margin: 0;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);
        }
        
        .add-charge-btn.add-charge-btn-green {
          background: linear-gradient(180deg, #22c55e 0%, #16a34a 100%);
          border-color: #15803d;
        }
        
        .add-charge-btn.add-charge-btn-green:hover {
          background: linear-gradient(180deg, #16a34a 0%, #15803d 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(22, 163, 74, 0.3);
        }
        
        .add-charge-btn:hover {
          background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35);
        }
        
        .add-charge-btn:active {
          transform: translateY(-2px) scale(0.98);
        }

        .client-charges-table-section {
          position: relative;
          max-width: none;
          margin-left: 0;
          margin-right: 0;
          width: 100%;
        }

        .client-charges-page .cc-charge-form-wrap {
          max-width: none;
          width: 100%;
          margin: 0 0 0.85rem;
        }

        .client-charges-page .cc-charge-form-card {
          background: #ffffff;
          border-radius: 12px;
          border: 1px solid #bfdbfe;
          box-shadow: 0 8px 24px rgba(37, 99, 235, 0.1);
          overflow: hidden;
        }

        .client-charges-page .cc-charge-form-head {
          background: linear-gradient(90deg, #1e3a8a 0%, #2563eb 50%, #3b82f6 100%);
          color: #ffffff;
          padding: 0.55rem 1rem;
          font-weight: 700;
          font-size: 0.88rem;
        }

        .client-charges-page .cc-charge-form-body {
          padding: 1rem 1.1rem;
        }

        .client-charges-page .cc-charge-form-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 0.75rem;
        }

        @media (max-width: 992px) {
          .client-charges-page .cc-charge-form-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 576px) {
          .client-charges-page .cc-charge-form-grid {
            grid-template-columns: 1fr;
          }
        }

        .client-charges-page .cc-charge-form-body .form-label {
          color: #334155;
          font-weight: 600;
          font-size: 0.8rem;
          margin-bottom: 0.25rem;
        }

        .client-charges-page .cc-charge-form-body .form-control,
        .client-charges-page .cc-charge-form-body .form-select {
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          font-size: 0.85rem;
        }

        .client-charges-page .cc-charge-form-body .form-control:focus,
        .client-charges-page .cc-charge-form-body .form-select:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
        }

        .client-charges-page .cc-charge-form-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-top: 0.85rem;
        }

        .client-charges-page .cc-btn-save {
          background: linear-gradient(180deg, #22c55e 0%, #16a34a 100%);
          border: 1px solid #15803d;
          color: #fff;
          border-radius: 8px;
          padding: 0.45rem 1rem;
          font-weight: 600;
          font-size: 0.82rem;
        }

        .client-charges-page .cc-btn-save:hover:not(:disabled) {
          filter: brightness(1.05);
        }

        .client-charges-page .cc-btn-cancel {
          background: #f1f5f9;
          border: 1px solid #cbd5e1;
          color: #475569;
          border-radius: 8px;
          padding: 0.45rem 1rem;
          font-weight: 600;
          font-size: 0.82rem;
        }

        .client-charges-page .cc-btn-cancel:hover {
          background: #e2e8f0;
        }

        .client-charges-page .cc-alert-success,
        .client-charges-page .cc-alert-danger {
          border-radius: 10px;
          padding: 0.55rem 0.85rem;
          margin-bottom: 0.75rem;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          border: none;
        }

        .client-charges-page .cc-alert-success {
          background: #ecfdf5;
          color: #166534;
          border-left: 4px solid #22c55e;
        }

        .client-charges-page .cc-alert-danger {
          background: #fef2f2;
          color: #991b1b;
          border-left: 4px solid #ef4444;
        }

        .client-charges-page .cc-empty-charges {
          padding: 1.5rem 1rem;
          text-align: center;
          color: #64748b;
          font-size: 0.88rem;
        }

        .client-charges-page .cc-empty-charges i {
          color: #3b82f6;
          font-size: 1.5rem;
          margin-bottom: 0.5rem;
        }

        @media (max-width: 576px) {
          .client-charges-page .client-charges-top-bar {
            flex-direction: row;
            align-items: flex-start;
            flex-wrap: wrap;
          }

          .client-charges-page .client-hono-recap-bar {
            flex: 1 1 auto;
            min-width: 0;
            width: auto;
          }
        }
        
        .charges-table-container {
          background: #ffffff;
          border-radius: 12px;
          padding: 0;
          box-shadow: 0 8px 24px rgba(37, 99, 235, 0.1);
          position: relative;
          overflow: visible;
          border: 1px solid #bfdbfe;
          max-width: none;
          margin-left: 0;
          margin-right: 0;
          width: 100%;
          min-width: 0;
        }

        .client-charges-page .client-charges-table-section {
          min-width: 0;
        }

        .client-charges-page .client-charges-table-scroll.table-responsive {
          display: block;
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow-x: auto !important;
          overflow-y: visible;
          -webkit-overflow-scrolling: touch;
          border: none;
          border-radius: 0 0 12px 12px;
          padding-bottom: 4px;
          background: #ffffff;
          scrollbar-color: #2563eb #e2e8f0;
          scrollbar-width: thin;
        }

        .client-charges-page .client-charges-table-scroll::-webkit-scrollbar {
          height: 10px;
        }

        .client-charges-page .client-charges-table-scroll::-webkit-scrollbar-track {
          background: #e5e7eb;
          border-radius: 999px;
        }

        .client-charges-page .client-charges-table-scroll::-webkit-scrollbar-thumb {
          background: #94a3b8;
          border-radius: 999px;
        }

        .client-charges-page .cc-th-short {
          display: none;
        }

        .client-charges-page .cc-btn-short {
          display: none;
        }

        .client-charges-page .modern-table.client-charges-table th.cc-col-actions,
        .client-charges-page .modern-table.client-charges-table td.cc-col-actions {
          min-width: 220px;
          width: auto;
          text-align: left;
        }

        .client-charges-page .modern-table.client-charges-table th.cc-col-actions {
          text-align: center;
        }

        .client-charges-page td.cc-col-actions .cc-actions-cell {
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .client-charges-page td.cc-col-actions .cc-actions-row {
          display: inline-flex;
          flex-direction: row;
          flex-wrap: nowrap;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: auto;
          margin: 0 auto;
        }

        .client-charges-page td.cc-col-actions .cc-actions-row > * {
          flex-shrink: 0;
        }

        .client-charges-page td.cc-col-actions .cc-actions-row .cc-action-delete {
          margin-left: 0;
          order: 0;
        }

        .client-charges-page td.cc-col-actions .cc-action-btn,
        .client-charges-page td.cc-col-actions .action-btn {
          flex-shrink: 0;
          white-space: nowrap;
        }

        .client-charges-page .modern-table.client-charges-table .btn-supprimer-hono {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          min-width: 34px;
          background: #ffffff;
          color: #dc2626;
          border: 1px solid #fee2e2;
          border-radius: 8px;
          padding: 0;
          cursor: pointer;
          flex-shrink: 0;
          transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
        }

        .client-charges-page .modern-table.client-charges-table .btn-supprimer-hono svg {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
        }

        .client-charges-page .modern-table.client-charges-table .btn-supprimer-hono:hover:not(:disabled) {
          background: #fef2f2;
          color: #b91c1c;
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12);
        }

        .client-charges-page .modern-table.client-charges-table .btn-supprimer-hono:disabled {
          background: #ffffff;
          color: #fca5a5;
          opacity: 0.45;
          cursor: not-allowed;
        }
        
        .charges-table-container::before {
          display: none;
        }
        
        .client-charges-page .charges-table-bar {
          background: linear-gradient(90deg, #1e3a8a 0%, #2563eb 100%);
          color: #ffffff;
          padding: 0.55rem 0.85rem;
          font-weight: 700;
          font-size: 0.82rem;
          border-radius: 12px 12px 0 0;
          text-align: center;
        }
        
        .charges-actions {
          background: transparent;
          border-radius: 0;
          padding: 0.35rem 0;
          box-shadow: none;
          border: none;
          backdrop-filter: none;
          margin-bottom: 0.35rem;
          position: absolute;
          top: 1px;
          right: 0;
          overflow: visible;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 0.35rem;
          flex-wrap: wrap;
          width: auto;
          z-index: 10;
        }
        
        .charges-actions-inline {
          display: flex;
          gap: 0.5rem;
          align-items: center;
          flex-wrap: wrap;
          margin: 0 auto;
          flex: 1 1 auto;
          justify-content: center;
          min-width: 200px;
        }
        
        .charges-actions-inline .add-charge-btn {
          margin: 0;
          white-space: nowrap;
          flex: 0 0 auto;
        }
        
        .charges-footer-print-btn {
          padding: 3px 8px !important;
          font-size: 0.72rem !important;
          font-weight: 600;
          width: auto !important;
          min-width: 0 !important;
        }
        
        .charges-table-title {
          color: #2c3e50;
          font-weight: 700;
          font-size: 0.9rem;
          margin: 0 0 0.2rem 0;
          text-align: center;
        }
        
        .charges-count-badge {
          background: linear-gradient(45deg, #0b5796, #0d6efd);
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;
          margin-left: 10px;
          box-shadow: 0 4px 10px rgba(11, 87, 150, 0.25);
        }
        
        .client-charges-page .modern-table.client-charges-table {
          width: 100%;
          border-collapse: collapse;
          border-spacing: 0;
          border-radius: 0;
          overflow: hidden;
          box-shadow: none;
          border: 1px solid #000000;
        }
        
        .client-charges-page .modern-table.client-charges-table thead {
          background: linear-gradient(90deg, #1e40af 0%, #2563eb 100%);
        }
        
        .client-charges-page .modern-table.client-charges-table th {
          color: #ffffff;
          font-weight: 700;
          padding: 0.5rem 0.4rem;
          text-align: center;
          border: 1px solid #000000;
          font-size: 0.72rem;
          letter-spacing: 0.02em;
        }
        
        .modern-table th:nth-child(2) {
          text-align: left;
          padding-left: 0.5rem;
        }
        
        .client-charges-page .modern-table.client-charges-table th:last-child {
          border-right: 1px solid #000000;
        }
        
        .modern-table tbody tr {
          background: #ffffff;
          transition: all 0.2s ease;
        }
        
        .modern-table tbody tr:nth-child(even) {
          background: #f8fafc;
        }
        
        .modern-table tbody tr:hover {
          background: #f0f6ff;
          box-shadow: none;
        }
        
        .client-charges-page .modern-table.client-charges-table td {
          padding: 0.35rem 0.4rem;
          text-align: center;
          border: 1px solid #000000;
          font-size: 0.78rem;
          font-weight: 500;
          color: #334155;
        }
        
        .modern-table td:nth-child(2) {
          text-align: left;
          padding-left: 0.5rem;
        }

        .client-charges-page .modern-table.client-charges-table td:nth-child(2) {
          white-space: normal;
          word-break: break-word;
        }
        
        .client-charges-page .modern-table.client-charges-table td:last-child {
          border-right: 1px solid #000000;
          background: inherit !important;
        }
        
        .client-charges-page .modern-table.client-charges-table td:last-child > div {
          background: transparent !important;
        }
        
        .client-charges-page .modern-table.client-charges-table tbody tr:last-child td {
          border-bottom: 1px solid #000000;
        }

        .client-charges-page .modern-table.client-charges-table tfoot th {
          border: 1px solid #000000;
        }
        
        .modern-table tbody tr.honoraire-avance-row {
          background-color: rgba(135, 206, 250, 0.2) !important;
        }
        
        .modern-table tbody tr.honoraire-avance-row:hover {
          background-color: rgba(135, 206, 250, 0.3) !important;
        }
        
        .modern-table tfoot {
          background: #f0f6ff !important;
        }
        
        .modern-table tfoot th,
        .modern-table tfoot td {
          background: #f0f6ff !important;
          color: #0b5796 !important;
          font-weight: 700;
          padding: 0.25rem 0.3rem;
          border-top: 2px solid #000000;
          border-right: 1px solid #000000;
          text-align: center;
          font-size: 0.75rem;
        }
        
        .client-charges-page .modern-table.client-charges-table tfoot th:last-child,
        .client-charges-page .modern-table.client-charges-table tfoot td:last-child {
          border-right: 1px solid #000000;
        }
        
        .action-btn {
          background: linear-gradient(45deg, #2196f3, #1976d2);
          border: none;
          color: white;
          border-radius: 10px;
          padding: 8px 16px;
          font-weight: 600;
          font-size: 0.8rem;
          transition: all 0.3s ease;
          margin: 0 2px;
          position: relative;
          overflow: hidden;
        }
        
        .action-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.5s;
        }
        
        .action-btn:hover::before {
          left: 100%;
        }
        
        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(33, 150, 243, 0.4);
        }
        
        .action-btn-success {
          background: linear-gradient(45deg, #4caf50, #45a049);
        }
        
        .action-btn-warning {
          background: linear-gradient(45deg, #ff9800, #f57c00);
        }
        
        .action-btn-danger {
          background: linear-gradient(45deg, #f44336, #d32f2f);
        }
        
        /* Style pour les boutons d'action principaux (Client, CGM, Client et CGM) */
        .btn-success {
          background: linear-gradient(45deg, #28a745, #20c997) !important;
          border: none !important;
          color: white !important;
          font-weight: 600 !important;
          transition: all 0.3s ease !important;
          box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3) !important;
        }
        
        .btn-success:hover:not(:disabled) {
          background: linear-gradient(45deg, #218838, #1e7e34) !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4) !important;
        }
        
        .btn-success:disabled {
          background: linear-gradient(45deg, #6c757d, #5a6268) !important;
          cursor: not-allowed !important;
          opacity: 0.7 !important;
        }
        
        /* Style pour le bouton "Reçu" - Exactement le même style que btn-primary (Bootstrap) mais en vert */
        /* Utiliser un sélecteur très spécifique pour surcharger le style général .btn-success */
        button.btn.btn-sm.btn-success[title="Imprimer le reçu"],
        .d-flex.gap-1 button.btn.btn-sm.btn-success[title="Imprimer le reçu"] {
          background-color: #0d6efd !important;
          background: #0d6efd !important;
          border-color: #0d6efd !important;
          border: 1px solid #0d6efd !important;
          color: #fff !important;
          font-weight: 400 !important;
          line-height: 1.5 !important;
          text-align: center !important;
          text-decoration: none !important;
          vertical-align: middle !important;
          cursor: pointer !important;
          user-select: none !important;
          padding: 0.25rem 0.5rem !important;
          font-size: 0.875rem !important;
          border-radius: 0.375rem !important;
          transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
          box-shadow: none !important;
          transform: none !important;
          min-width: 30px !important;
          width: auto !important;
        }
        
        /* Forcer la couleur verte au lieu du bleu */
        button.btn.btn-sm.btn-success[title="Imprimer le reçu"],
        .d-flex.gap-1 button.btn.btn-sm.btn-success[title="Imprimer le reçu"] {
          background-color: #198754 !important;
          background: #198754 !important;
          border-color: #198754 !important;
          border: 1px solid #198754 !important;
        }
        
        button.btn.btn-sm.btn-success[title="Imprimer le reçu"]:hover:not(:disabled),
        .d-flex.gap-1 button.btn.btn-sm.btn-success[title="Imprimer le reçu"]:hover:not(:disabled) {
          background-color: #157347 !important;
          background: #157347 !important;
          border-color: #146c43 !important;
          border: 1px solid #146c43 !important;
          color: #fff !important;
          transform: none !important;
          box-shadow: 0 0 0 0.25rem rgba(25, 135, 84, 0.25) !important;
        }
        
        button.btn.btn-sm.btn-success[title="Imprimer le reçu"]:focus:not(:disabled),
        .d-flex.gap-1 button.btn.btn-sm.btn-success[title="Imprimer le reçu"]:focus:not(:disabled) {
          background-color: #157347 !important;
          background: #157347 !important;
          border-color: #146c43 !important;
          border: 1px solid #146c43 !important;
          box-shadow: 0 0 0 0.25rem rgba(25, 135, 84, 0.5) !important;
          color: #fff !important;
        }
        
        /* Style pour les boutons d'action plus petits */
        .action-btn-sm {
          font-size: 0.65rem !important;
          padding: 0.2rem 0.4rem !important;
          min-width: 50px !important;
        }
        
        .action-btn-sm i {
          font-size: 0.65rem !important;
        }
        
        /* Réduire la taille des boutons dans le tableau */
        .modern-table .action-btn,
        .modern-table .action-btn-sm {
          font-size: 0.65rem !important;
          padding: 0.2rem 0.4rem !important;
          min-width: auto;
        }
        
        .modern-table .action-btn i,
        .modern-table .action-btn-sm i {
          font-size: 0.6rem !important;
          margin-right: 2px !important;
        }
        
        .text-success {
          color: #4caf50 !important;
          font-weight: 600;
        }
        
        .text-danger {
          color: #f44336 !important;
          font-weight: 600;
        }
        
        .text-primary {
          color: #2196f3 !important;
          font-weight: 600;
        }
        
        .modern-spinner {
          width: 24px;
          height: 24px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top: 3px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          display: inline-block;
          margin-right: 10px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .container {
          margin-top: 0;
          padding-top: 0;
          position: relative;
        }
        
        /* Animations d'entrée */
        .container > * {
          animation: fadeInUp 0.8s ease-out both;
        }
        
        .container > *:nth-child(1) { animation-delay: 0.1s; }
        .container > *:nth-child(2) { animation-delay: 0.2s; }
        .container > *:nth-child(3) { animation-delay: 0.3s; }
        .container > *:nth-child(4) { animation-delay: 0.4s; }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Responsive Design */
        @media (max-width: 992px) {
          .charges-table-container {
            margin: 0.5rem;
            padding: 0.35rem;
          }
          
          .client-charges-page .client-charges-table-scroll.table-responsive {
            overflow-x: auto !important;
            width: 100%;
            max-width: 100%;
          }
          
          .client-charges-page .modern-table.client-charges-table {
            min-width: 540px;
            width: max-content;
            display: table;
            table-layout: fixed;
          }
          
          .client-charges-page .modern-table.client-charges-table th,
          .client-charges-page .modern-table.client-charges-table td {
            padding: 0.22rem 0.18rem;
            white-space: nowrap;
            font-size: 0.68rem;
          }
     @media (max-width: 768px) {
  .client-charges-page .client-charges-table-scroll.table-responsive {
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important; /* Pour iOS */
    touch-action: pan-x pan-y !important; /* Permet le scroll horizontal ET vertical tactile */
    overscroll-behavior-y: auto !important;
    margin: 0;
    padding: 0 0 4px;
  }
}
          .client-charges-page .modern-table.client-charges-table .cc-col-libelle {
            white-space: normal;
            word-break: break-word;
          }
          
          .add-charge-form-row {
            flex-direction: column;
          }
          
          .add-charge-form-field {
            min-width: 100%;
          }
        }
        
        @media (max-width: 768px) {
          .charges-title { font-size: 2.2rem; }
          .charges-header, .charges-table-container {
            margin: 0.5rem;
            padding: 1rem;
          }
          .client-info-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
          .client-charges-page {
            padding: 0.5rem 0;
          }
          
          .client-info-card {
            padding: 0.75rem 1rem !important;
          }
          
          .client-info-summary {
            flex-direction: column;
            align-items: stretch;
            gap: 0.75rem;
          }
          
          .client-info-left {
            width: 100%;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }
          
          .client-info-year {
            width: 100%;
            margin-left: 0 !important;
            justify-content: space-between;
            order: 2;
          }
          
          .charges-actions-inline {
            width: auto;
            margin: 0.5rem 0;
            justify-content: center;
            order: 3;
          }
          
          .charges-actions-inline .add-charge-btn {
            flex: 0 0 auto;
            min-width: 0;
            font-size: 0.72rem;
            padding: 4px 8px;
          }
          
          .charges-footer-print-btn {
            padding: 3px 8px !important;
            font-size: 0.7rem !important;
          }
          
          .client-charges-page .container {
            max-width: 100%;
            min-width: 0;
            padding-left: 0.35rem;
            padding-right: 0.35rem;
          }

          .client-charges-page .client-charges-table-scroll.table-responsive {
            margin: 0;
            padding: 0 0 4px;
          }
          
          .client-charges-page .modern-table.client-charges-table {
            font-size: 0.65rem;
            min-width: 500px;
            width: max-content;
          }

          .client-charges-page .modern-table.client-charges-table th,
          .client-charges-page .modern-table.client-charges-table td {
            padding: 0.18rem 0.14rem;
            font-size: 0.62rem;
          }

          .client-charges-page .modern-table.client-charges-table .cc-th-icon {
            display: none;
          }

          .client-charges-page .modern-table.client-charges-table .cc-th-long {
            display: none;
          }

          .client-charges-page .modern-table.client-charges-table .cc-th-short {
            display: inline;
          }

          .client-charges-page .modern-table.client-charges-table .cc-col-date {
            width: 68px;
            min-width: 68px;
          }

          .client-charges-page .modern-table.client-charges-table .cc-col-libelle {
            width: 28%;
            min-width: 88px;
            max-width: 120px;
          }

          .client-charges-page .modern-table.client-charges-table .cc-col-montant,
          .client-charges-page .modern-table.client-charges-table .cc-col-avance,
          .client-charges-page .modern-table.client-charges-table .cc-col-reste {
            width: 72px;
            min-width: 72px;
          }

          .client-charges-page .modern-table.client-charges-table th.cc-col-actions,
          .client-charges-page .modern-table.client-charges-table td.cc-col-actions {
            width: auto;
            min-width: 140px;
            max-width: none;
          }

          .client-charges-page td.cc-col-actions {
            padding: 0.1rem 0.06rem !important;
            vertical-align: middle;
          }

          .client-charges-page td.cc-col-actions .cc-actions-row {
            flex-wrap: nowrap !important;
            justify-content: center !important;
            gap: 6px !important;
          }

          .client-charges-page td.cc-col-actions .cc-actions-row .cc-action-delete {
            margin-left: 0 !important;
          }

          .client-charges-page td.cc-col-actions .cc-action-btn,
          .client-charges-page td.cc-col-actions .action-btn {
            display: inline-flex;
            width: auto;
            max-width: none;
            font-size: 0.46rem !important;
            padding: 1px 2px !important;
            min-width: 0 !important;
            min-height: 0 !important;
            line-height: 1.1 !important;
            border-radius: 3px !important;
            margin: 0 !important;
            font-weight: 600 !important;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            box-shadow: none !important;
            transform: none !important;
            background-image: none !important;
          }

          .client-charges-page .modern-table.client-charges-table .btn-supprimer-hono {
            width: 26px !important;
            height: 26px !important;
            min-width: 26px !important;
            border-radius: 6px !important;
          }

          .client-charges-page .modern-table.client-charges-table .btn-supprimer-hono svg {
            width: 14px !important;
            height: 14px !important;
          }

          .client-charges-page td.cc-col-actions .cc-action-btn i,
          .client-charges-page td.cc-col-actions .action-btn i {
            display: none !important;
          }

          /* Surcharger les styles spécifiques du bouton Reçu (padding/font hérités) */
          .client-charges-page td.cc-col-actions .cc-action-btn-recu,
          .client-charges-page td.cc-col-actions button.btn.btn-sm.btn-success[title="Imprimer le reçu"] {
            font-size: 0.46rem !important;
            padding: 1px 2px !important;
            line-height: 1.1 !important;
            min-width: 0 !important;
            width: auto !important;
            min-height: 0 !important;
            border-radius: 3px !important;
            font-weight: 600 !important;
            box-shadow: none !important;
            transform: none !important;
            background: #198754 !important;
            background-color: #198754 !important;
            border: 1px solid #157347 !important;
          }

          .client-charges-page td.cc-col-actions .cc-action-btn-recu:hover:not(:disabled),
          .client-charges-page td.cc-col-actions button.btn.btn-sm.btn-success[title="Imprimer le reçu"]:hover:not(:disabled) {
            background: #157347 !important;
            background-color: #157347 !important;
            transform: none !important;
            box-shadow: none !important;
          }

          .client-charges-page td.cc-col-actions .cc-action-btn.btn-success {
            background: #28a745 !important;
            background-image: none !important;
            border: none !important;
            box-shadow: none !important;
          }

          .client-charges-page td.cc-col-actions .text-muted.fst-italic {
            font-size: 0.44rem !important;
            line-height: 1.1;
            display: block;
            text-align: center;
          }

          .client-charges-page .cc-btn-full {
            display: none;
          }

          .client-charges-page .cc-btn-short {
            display: inline;
          }
        }
        
        @media (max-width: 480px) {
          .charges-title { font-size: 1.8rem; }
          .charges-header, .charges-table-container {
            padding: 0.75rem;
            margin: 0.25rem;
          }
          
          .client-info-card {
            padding: 0.75rem !important;
            margin: 0.5rem 0.25rem !important;
          }
          
          .client-info-summary {
            flex-direction: column;
            align-items: stretch;
            gap: 0.75rem;
          }
          
          .client-info-left {
            width: 100%;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }
          
          .client-info-text {
            font-size: 0.75rem;
          }
          
          .charges-actions-inline {
            width: auto;
            margin: 0.5rem 0;
            flex-direction: row;
            gap: 0.35rem;
          }
          
          .charges-actions-inline .add-charge-btn {
            width: auto;
            min-width: 0;
            padding: 3px 6px;
            font-size: 0.7rem;
            margin: 0;
          }
          
          .charges-footer-print-btn {
            padding: 2px 6px !important;
            font-size: 0.65rem !important;
          }
          
          .client-info-year {
            width: 100%;
            margin-left: 0 !important;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }
          
          .client-info-year select {
            width: 100%;
            padding: 8px;
            font-size: 0.9rem;
          }
          
          .client-charges-page .client-charges-table-scroll.table-responsive {
            margin: 0;
          }
            
          
          .client-charges-page .modern-table.client-charges-table {
            font-size: 0.6rem;
            min-width: 460px;
          }
          
          .client-charges-page .modern-table.client-charges-table th,
          .client-charges-page .modern-table.client-charges-table td {
            padding: 0.14rem 0.1rem;
            font-size: 0.58rem;
          }

          .client-charges-page .modern-table.client-charges-table .cc-col-libelle {
            min-width: 76px;
            max-width: 100px;
          }

          .client-charges-page .modern-table.client-charges-table th.cc-col-actions,
          .client-charges-page .modern-table.client-charges-table td.cc-col-actions {
            width: auto;
            min-width: 120px;
            max-width: none;
          }

          .client-charges-page td.cc-col-actions .cc-action-btn,
          .client-charges-page td.cc-col-actions .action-btn,
          .client-charges-page td.cc-col-actions .cc-action-btn-recu,
          .client-charges-page td.cc-col-actions button.btn.btn-sm.btn-success[title="Imprimer le reçu"] {
            font-size: 0.42rem !important;
            padding: 1px 1px !important;
            line-height: 1.05 !important;
          }
        }
      `}</style>

      <div className="container-fluid px-0">
        <div className="client-charges-top-bar">
          <button
            type="button"
            className="cgm-toolbar-btn cgm-toolbar-btn--back cc-back-btn"
            onClick={() => navigate('/clients')}
            title="Retour à la liste des clients"
          >
            <i className="fas fa-arrow-left" /> Retour
          </button>
          {honoRecap || fondClientRecap ? (
            <div className="cc-info-cards" role="status" aria-live="polite">
              {honoRecap && (
                <div className="cc-info-card cc-info-card--hono">
                  <p className="cc-info-card__text">
                    Hono de <strong>{client?.nom || '…'}</strong>
                    <span className="cc-info-card__sep">·</span>
                    <span className="cc-info-card__pill">{HONO_PERIODE_LABELS[honoRecap.periode_type]}</span>
                    <span className="cc-info-card__sep">·</span>
                    montant <strong className="cc-info-card__amount">{honoRecap.montantFormatted}</strong>
                  </p>
                </div>
              )}
              {fondClientRecap && (
                <div className="cc-info-card cc-info-card--fond">
                  <p className="cc-info-card__text">
                    Fond client chez CGM de <strong>{client?.nom || '…'}</strong>
                    <span className="cc-info-card__sep">·</span>
                    solde{' '}
                    <strong className={`cc-info-card__amount${fondClientRecap.isNegative ? ' is-neg' : ''}`}>
                      {fondClientRecap.isNegative ? '−' : ''}{fondClientRecap.montantFormatted}
                    </strong>
                  </p>
                </div>
              )}
            </div>
          ) : null}
        </div>
        <div className="cgm-list-toolbar cc-modern-toolbar">
          <div className="cgm-page-head cc-charges-page-head">
            <div className="cgm-page-title-wrap">
              <span className="cgm-page-title">Charges mensuelles</span>
              <span className="cgm-count-badge" title={`${totalFilteredCharges} ligne(s)`}>
                {totalFilteredCharges}
              </span>
            </div>
          </div>

          <div className="cgm-filter-panel cc-filter-bar">
            <div className="cc-filter-bar__main">
              <div className="cgm-filter-field cc-filter-field--client">
                <label>Client</label>
                <span className="cc-client-filter-value">
                  {client.username} — {client?.nom} {client?.prenom || ''}
                </span>
              </div>
              <div className="cgm-filter-field">
                <label htmlFor="cc-filter-annee">Année</label>
                <select
                  id="cc-filter-annee"
                  value={annee}
                  onChange={(e) => setAnnee(parseInt(e.target.value, 10))}
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="cgm-filter-field">
                <label htmlFor="cc-filter-libelle">Libellé</label>
                <select
                  id="cc-filter-libelle"
                  value={filterLibelle}
                  onChange={(e) => setFilterLibelle(e.target.value)}
                >
                  <option value="">Tous</option>
                  {baseLibelles.map((libelle) => (
                    <option key={libelle} value={libelle}>{libelle}</option>
                  ))}
                </select>
              </div>

              <div className="cc-filter-bar__actions" role="group" aria-label="Actions">
                <button
                  type="button"
                  className="cgm-toolbar-btn cgm-toolbar-btn--add"
                  onClick={() => {
                    setIsCarteBancaire(false);
                    setShowForm(true);
                  }}
                >
                  <i className="fas fa-plus" aria-hidden="true" />
                  Ajouter une opération
                </button>
                <button
                  type="button"
                  className="cgm-toolbar-btn cgm-toolbar-btn--print"
                  onClick={() => window.open(`/client/${id}/charges/print?annee=${annee}`, '_blank')}
                  title="Imprimer la liste des charges"
                >
                  <i className="fas fa-print" aria-hidden="true" />
                  Imprimer
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Message de succès */}
        {successMessage && (
          <div className="cc-alert-success" role="alert">
            <i className="fas fa-check-circle" aria-hidden="true" />
            <span style={{ flex: 1 }}>{successMessage}</span>
            <button type="button" className="btn-close" onClick={() => setSuccessMessage('')} aria-label="Fermer" />
          </div>
        )}

        {errorMessage && (
          <div className="cc-alert-danger" role="alert">
            <i className="fas fa-exclamation-circle" aria-hidden="true" />
            <span style={{ flex: 1 }}>{errorMessage}</span>
            <button type="button" className="btn-close" onClick={() => setErrorMessage('')} aria-label="Fermer" />
          </div>
        )}


        {showForm && (
          <div className="cc-charge-form-wrap">
            <div className="cc-charge-form-card">
              <div className="cc-charge-form-head">
                {isCarteBancaire ? 'Ajouter une charge (Carte bancaire)' : 'Ajouter une opération'}
              </div>
              <div className="cc-charge-form-body">
                <ClientOperationAddForm
                  clientId={parseInt(id, 10)}
                  annee={annee}
                  isCaissier={isCaissier()}
                  getCaissierDateOptions={getCaissierDateOptions}
                  getCaissierDateValueForSelect={getCaissierDateValueForSelect}
                  isCarteBancaire={isCarteBancaire}
                  fondClientSolde={fondClientRecap ? fondClientRecap.solde : null}
                  onSuccess={async (data) => {
                    const op = data.operation || {};
                    const finalLibelle = op.libelle || '';
                    const chargeMontant = parseFloat(op.debit || 0);
                    const chargeAvance = parseFloat(op.credit || 0);

                    setSuccessMessage('Opération ajoutée avec succès !');
                    setShowForm(false);
                    setIsCarteBancaire(false);
                    fetchClientCharges();

                    window.dispatchEvent(new CustomEvent('caisse-updated'));
                    window.dispatchEvent(new CustomEvent('charge-added'));
                    window.dispatchEvent(new CustomEvent('charge-updated'));
                    window.dispatchEvent(new CustomEvent('etat-client-updated', {
                      detail: { clientId: id },
                    }));

                    setTimeout(() => setSuccessMessage(''), 3000);
                  }}
                  onCancel={() => {
                    setShowForm(false);
                    setIsCarteBancaire(false);
                  }}
                  onError={(msg) => setErrorMessage(msg)}
                />
              </div>
            </div>
          </div>
        )}

        {canEditOperations && showEditForm && editingCharge && (
          <div
            className="modal-overlay cc-edit-modal-overlay"
            onClick={() => {
              setShowEditForm(false);
              setEditingCharge(null);
            }}
          >
            <div className="cc-edit-modal" onClick={(e) => e.stopPropagation()}>
              <h5 className="cc-edit-modal__title">
                <i className="fas fa-edit" aria-hidden="true" />
                Modifier l&apos;opération
              </h5>
              <div className="cc-edit-modal__form">
                <ClientOperationAddForm
                  clientId={parseInt(id, 10)}
                  annee={annee}
                  isCaissier={isCaissier()}
                  getCaissierDateOptions={getCaissierDateOptions}
                  getCaissierDateValueForSelect={getCaissierDateValueForSelect}
                  editId={editingCharge.id}
                  fondClientSolde={fondClientRecap ? fondClientRecap.solde : null}
                  onSuccess={async (data) => {
                    setSuccessMessage('Opération modifiée avec succès !');
                    setShowEditForm(false);
                    setEditingCharge(null);
                    fetchClientCharges();

                    window.dispatchEvent(new CustomEvent('caisse-updated'));
                    window.dispatchEvent(new CustomEvent('charge-updated'));
                    window.dispatchEvent(new CustomEvent('etat-client-updated', {
                      detail: { clientId: id },
                    }));
                    setTimeout(() => setSuccessMessage(''), 3000);
                  }}
                  onCancel={() => {
                    setShowEditForm(false);
                    setEditingCharge(null);
                  }}
                  onError={(msg) => setErrorMessage(msg || 'Erreur lors de la modification')}
                />
              </div>
            </div>
          </div>
        )}

        <div className="nh-modern-table-card cc-charges-table-card">
          {filteredCharges.length === 0 ? (
            <div className="cc-empty-charges">
              <div><i className="fas fa-info-circle" aria-hidden="true" /></div>
              <p className="mb-1 fw-semibold">{filterLibelle ? `Aucune charge avec le libellé « ${filterLibelle} »` : 'Aucune charge enregistrée pour ce client'}</p>
              <p className="mb-0 small">{filterLibelle ? 'Essayez un autre libellé ou « Tous ».' : 'Utilisez « Ajouter une charge » pour commencer.'}</p>
            </div>
          ) : (
            <>
              <div className="nh-modern-table-toolbar">
                <span className="nh-modern-table-info">
                  Charges — {client?.nom} {client?.prenom} ({annee})
                </span>
                <label className="nh-modern-table-length" htmlFor="cc-charges-page-size">
                  Afficher
                  <select
                    id="cc-charges-page-size"
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  lignes
                </label>
              </div>

              <div className="overview-table-container nh-modern-table-scroll">
              <table className="overview-table nh-overview-data-table nh-modern-data-table client-charges-modern-table">
                <thead>
                  <tr>
                    <th className="cc-col-date">
                      <span className="nh-modern-th-inner">Date</span>
                    </th>
                    <th className="cc-col-libelle">
                      <span className="nh-modern-th-inner">Libellé</span>
                    </th>
                    <th className="cc-col-operation">
                      <span className="nh-modern-th-inner">Opération</span>
                    </th>
                    <th className="cc-col-mode">
                      <span className="nh-modern-th-inner">Type d'opération</span>
                    </th>
                    <th className="cc-col-montant">
                      <span className="nh-modern-th-inner">Débit</span>
                    </th>
                    <th className="cc-col-avance">
                      <span className="nh-modern-th-inner">Crédit</span>
                    </th>
                    <th className="cc-col-reste">
                      <span className="nh-modern-th-inner">Reste</span>
                    </th>
                    <th className="cc-col-actions">
                      <span className="nh-modern-th-inner">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagedCharges.map((charge) => {
                    // Calculer le solde cumulatif pour cette ligne
                    // Pour le calcul correct, on doit utiliser toutes les charges jusqu'à cette charge
                    // Trouver l'index de cette charge dans la liste complète
                    const chargeIndexInFullList = charges.findIndex(c => c.id === charge.id);

                    let soldeCumulatif = 0;

                    // Si c'est le solde reporté, utiliser sa valeur réelle
                    if (charge.isPrecedent) {
                      soldeCumulatif = parseFloat(charge.solde_restant || 0);
                    } else {
                      // Trouver le solde reporté (stock initial) pour l'inclure dans le calcul
                      let stockInitial = 0;
                      const soldeReporte = charges.find(c => c.isPrecedent);
                      if (soldeReporte) {
                        stockInitial = parseFloat(soldeReporte.solde_restant || 0);
                      }

                      // Calculer la somme cumulative jusqu'à cette ligne dans la liste complète
                      // Logique: Stock initial + Avances - Montants
                      soldeCumulatif = stockInitial;
                      for (let i = 0; i <= chargeIndexInFullList; i++) {
                        const currentCharge = charges[i];
                        if (currentCharge.isPrecedent) {
                          // Ignorer le solde reporté car il est déjà inclus dans stockInitial
                          continue;
                        }
                        const montant = parseFloat(currentCharge.montant || 0);
                        const avance = parseFloat(currentCharge.avance || 0);
                        // Ajouter l'avance et soustraire le montant
                        soldeCumulatif += avance - montant;
                      }
                    }

                    // Vérifier si c'est un honoraire reçu ou une avance
                    const chargeLibelle = (charge.libelle || '').toUpperCase();
                    const isHonoraireRecuCheck = isHonoraireRecu(charge.libelle);
                    const isAvance = isAvanceType(charge.libelle);
                    const isHonoraireOrAvance = isHonoraireRecuCheck || isAvance;

                    const rowClass = charge.isPrecedent
                      ? 'cc-row-precedent'
                      : (isHonoraireOrAvance ? 'cc-row-hono-avance' : '');

                    return (
                      <tr
                        key={charge.id}
                        className={rowClass}
                        style={{ cursor: charge.isPrecedent ? 'default' : 'pointer' }}
                        onClick={(e) => {
                          // Ne pas ouvrir le formulaire pour le solde reporté
                          // Ne pas permettre la modification pour les caissiers (users)
                          if (!charge.isPrecedent && canEditOperations) {
                            // Ne pas déclencher si on clique sur un bouton ou dans la colonne ACTIONS
                            const target = e.target;
                            const clickedCell = target.closest('td');
                            const isLastCell = clickedCell && clickedCell === clickedCell.parentElement.lastElementChild;

                            if (target.tagName === 'BUTTON' ||
                              target.closest('button') ||
                              target.closest('.d-flex') ||
                              isLastCell) {
                              return;
                            }
                            handleEditCharge(charge);
                          }
                        }}
                      >
                        <td className="cc-col-date">
                          {charge.date ? formatDateForDisplay(charge.date) : formatDateForDisplay(charge.date_creation)}
                        </td>
                        <td className="cc-col-libelle">
                          {formatLibelleForDisplay(charge.libelle)}
                          {isAutoGeneratedHonoLibelle(charge.libelle) && (
                            <span className="charge-hono-auto-badge" title="Honoraire généré automatiquement">
                              Auto
                            </span>
                          )}
                        </td>
                        <td className="cc-col-operation">
                          {charge.isPrecedent
                            ? '—'
                            : charge.operation_side === 'credit'
                              ? 'Crédit'
                              : charge.operation_side === 'debit'
                                ? 'Débit'
                                : '—'}
                        </td>
                        <td className="cc-col-mode">
                          {charge.isPrecedent ? '—' : (charge.detail_label || '—')}
                        </td>
                        <td className="cc-col-montant">{renderMontantGreen(charge.montant)}</td>
                        <td className="cc-col-avance">{renderAvanceRed(charge.avance || 0)}</td>
                        <td className="cc-col-reste">
                          {charge.isPrecedent
                            ? renderReste(parseFloat(charge.solde_restant || 0))
                            : renderReste(soldeCumulatif)}
                        </td>
                        <td className="cc-col-actions">
                          {charge.isPrecedent ? (
                            <span className="text-muted fst-italic">Solde reporté</span>
                          ) : (
                            <div
                              className="cc-actions-cell"
                              role="group"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="cc-actions-row">
                                {(charge.operation_side === 'credit' ||
                                  parseFloat(charge.avance || 0) > 0 ||
                                  isHonoraireRecu(charge.libelle) ||
                                  isAvanceType(charge.libelle)) && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-success cc-action-btn cc-action-btn-recu"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePrintCharge(charge);
                                    }}
                                    title="Imprimer le reçu"
                                  >
                                    <span className="cc-btn-full">Reçu</span>
                                    <span className="cc-btn-short">Reç.</span>
                                  </button>
                                )}
                                {canEditOperations && (
                                  <>
                                    <button
                                      type="button"
                                      className="nh-action-icon-btn nh-action-icon-btn--edit cc-action-edit"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditCharge(charge);
                                      }}
                                      title="Modifier l'opération"
                                      aria-label="Modifier l'opération"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                      </svg>
                                    </button>
                                    <button
                                      type="button"
                                      className="nh-action-icon-btn nh-action-icon-btn--delete cc-action-delete"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteCharge(charge.id);
                                      }}
                                      title="Supprimer la charge"
                                      aria-label="Supprimer la charge"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <polyline points="3 6 5 6 21 6" />
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        <line x1="10" y1="11" x2="10" y2="17" />
                                        <line x1="14" y1="11" x2="14" y2="17" />
                                      </svg>
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {(() => {
                    // Calculer le total des charges filtrées
                    // Pour le reste, on doit utiliser toutes les charges jusqu'à la dernière charge filtrée
                    const lastFilteredCharge = filteredCharges[filteredCharges.length - 1];
                    const lastIndexInFullList = lastFilteredCharge ? charges.findIndex(c => c.id === lastFilteredCharge.id) : -1;

                    let totalReste = 0;
                    const soldeReporte = charges.find(c => c.isPrecedent);
                    if (soldeReporte) {
                      totalReste = parseFloat(soldeReporte.solde_restant || 0);
                    }
                    // Calculer le reste jusqu'à la dernière charge filtrée dans la liste complète
                    if (lastIndexInFullList >= 0) {
                      totalReste += charges.slice(0, lastIndexInFullList + 1).reduce((sum, c) => {
                        if (c.isPrecedent) return sum; // Ignorer le solde reporté (déjà ajouté)
                        return sum + parseFloat(c.avance || 0) - parseFloat(c.montant);
                      }, 0);
                    }

                    // Calculer le total des dépenses uniquement (montant > 0 et avance = 0, excluant honoraires reçus et avances)
                    // Ce total doit correspondre au total des dépenses dans l'état client
                    const totalDepenses = filteredCharges.reduce((sum, c) => {
                      const chargeLibelle = (c.libelle || '').toUpperCase();
                      const isHonoraireRecuCheck = isHonoraireRecu(c.libelle);
                      const isAvance = isAvanceType(c.libelle);
                      const isDepense = parseFloat(c.montant || 0) > 0 && parseFloat(c.avance || 0) === 0;
                      // Ne compter que les dépenses (pas les honoraires reçus ni les avances)
                      if (isDepense && !isHonoraireRecuCheck && !isAvance) {
                        return sum + parseFloat(c.montant || 0);
                      }
                      return sum;
                    }, 0);

                    // Calculer le total de tous les montants (pour référence, mais on affiche le total des dépenses)
                    const totalTousMontants = filteredCharges.reduce((sum, c) => sum + parseFloat(c.montant || 0), 0);

                    return (
                      <tr>
                        <th className="cc-col-date">TOTAL</th>
                        <th className="cc-col-libelle"></th>
                        <th className="cc-col-operation"></th>
                        <th className="cc-col-mode"></th>
                        <th className="cc-col-montant" title={`Total dépenses: ${formatMontant(totalDepenses)} | Total tous montants: ${formatMontant(totalTousMontants)}`}>
                          {formatMontant(totalDepenses)}
                        </th>
                        <th className="cc-col-avance">{formatMontant(filteredCharges.reduce((sum, c) => sum + parseFloat(c.avance || 0), 0))}</th>
                        <th className="cc-col-reste">
                          {renderReste(totalReste)}
                        </th>
                        <th className="cc-col-actions"></th>
                      </tr>
                    );
                  })()}
                </tfoot>
              </table>
              </div>

              <div className="nh-modern-table-footer">
                <div className="nh-modern-table-info">
                  Affichage de {totalFilteredCharges === 0 ? 0 : indexOfFirstItem + 1} à {Math.min(indexOfLastItem, totalFilteredCharges)} sur {totalFilteredCharges} enregistrement{totalFilteredCharges !== 1 ? 's' : ''}
                </div>
                <div className="nh-modern-pagination">
                  <button
                    type="button"
                    className="nh-modern-page-nav"
                    onClick={goToPrevPage}
                    disabled={currentPage === 1}
                  >
                    Précédent
                  </button>
                  <div className="nh-modern-page-numbers">
                    {getChargesPageNumbers(currentPage, totalPages).map((page, index) => (
                      page === '...' ? (
                        <span key={`dots-${index}`} className="nh-modern-page-dots">…</span>
                      ) : (
                        <button
                          key={`page-${page}`}
                          type="button"
                          className={`nh-modern-page-btn${currentPage === page ? ' is-active' : ''}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      )
                    ))}
                  </div>
                  <button
                    type="button"
                    className="nh-modern-page-nav"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Suivant
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

